// ═══════════════════════════════════════════════════════════════
// PIXALLOY ENGINE — mkEngine (all command handlers, render, tick)
// ═══════════════════════════════════════════════════════════════
import { C, T, FLT, ADJ, ADJ_DEFAULTS, ADJ_LABELS, BLEND_MAP, TIP_NAMES } from './constants.js';
import { mkL, mkAdjL, mkGroupL, mkVectorL, applyAdjustment } from './core.js';
import { GLCompositor } from '@alloyhead/core/render';
import { RenderCache } from '@alloyhead/core/render';

export function mkEngine(W,H){
  const renderCache=new RenderCache(); // Alloyhead framework render cache
  const state={w:W,h:H,layers:[mkL("Background",W,H,true)],act:0,sel:null,tfm:null,clip:null,
    strokeOn:false,lastPt:null,undo:[],redo:[],p:{},texts:[],history:[],clipOverlay:false,stampSrc:null,stampCompDirty:false,stampComp:null,
    onDimsChanged:null,onHiddenLayerAttempt:null,_bridge:null};
  state.layers[0].x.fillStyle="#fff";state.layers[0].x.fillRect(0,0,W,H);
  state.history.push({icon:"🖼",label:"New Canvas"});

  // ── Delta-based undo (v0.8) ──
  // Only store full pixel data for layers that were modified since last snap.
  // Unchanged layers reuse their previous snapshot data (shared reference, no copy).
  // Shared pool: reuse the last snapshot's data for unchanged layers
  let _prevSnapshotLayers=null;

  const takeSnapshot=()=>{
    const layerSnaps=state.layers.map((l,li)=>{
      const props={fx:{...l.fx},op:l.op,blend:l.blend,vis:l.vis,name:l.name,maskEnabled:l.maskEnabled,maskLinked:l.maskLinked,textData:l.textData?{...l.textData}:null,_psdTextData:l._psdTextData||null,type:l.type||"raster",adjType:l.adjType||null,adjParams:l.adjParams?{...l.adjParams}:null,children:l.children?l.children.map(ci=>ci):null,collapsed:l.collapsed||false,vectorData:l.vectorData?{paths:JSON.parse(JSON.stringify(l.vectorData.paths)),style:{...l.vectorData.style}}:null};

      // Fast path: if layer pixel data hasn't been touched since last snap, reuse previous data
      if(l._undoClean&&_prevSnapshotLayers&&_prevSnapshotLayers[li]&&_prevSnapshotLayers[li].d){
        props.d=_prevSnapshotLayers[li].d;
        props.md=_prevSnapshotLayers[li].md;
      } else {
        // Layer was modified — read and store new data
        const d=l.x.getImageData(0,0,state.w,state.h);
        const md=l.maskCtx.getImageData(0,0,state.w,state.h);
        props.d=d;
        props.md=md;
      }
      l._undoClean=true; // Mark clean until next modification

      return props;
    });
    _prevSnapshotLayers=layerSnaps;
    return{w:state.w,h:state.h,act:state.act,sel:state.sel?{...state.sel,mask:new Uint8Array(state.sel.mask)}:null,layers:layerSnaps};
  };

  // Full world restore: rebuilds layers at correct dimensions, restores selection + active index
  const restoreSnapshot=(sn)=>{
    const oldW=state.w,oldH=state.h;
    state.w=sn.w;state.h=sn.h;
    // Resize layer array
    while(state.layers.length>sn.layers.length)state.layers.pop();
    while(state.layers.length<sn.layers.length)state.layers.push(mkL("Restored",sn.w,sn.h));
    // Restore each layer — resize canvases if dimensions changed
    for(let i=0;i<sn.layers.length;i++){
      const ld=sn.layers[i],l=state.layers[i];
      if(l.c.width!==sn.w||l.c.height!==sn.h){
        l.c.width=sn.w;l.c.height=sn.h;
        l.x=l.c.getContext("2d",{willReadFrequently:true});
        l.mask.width=sn.w;l.mask.height=sn.h;
        l.maskCtx=l.mask.getContext("2d",{willReadFrequently:true});
      }
      l.x.putImageData(ld.d,0,0);
      l.maskCtx.putImageData(ld.md,0,0);
      l.fx={...ld.fx};l.op=ld.op;l.blend=ld.blend;l.vis=ld.vis;l.name=ld.name;
      l.maskEnabled=ld.maskEnabled;l.maskLinked=ld.maskLinked;l.textData=ld.textData?{...ld.textData}:null;l._psdTextData=ld._psdTextData||null;
      l.type=ld.type||"raster";l.adjType=ld.adjType||null;l.adjParams=ld.adjParams?{...ld.adjParams}:null;
      l.children=ld.children?ld.children.slice():null;l.collapsed=ld.collapsed||false;
      l.vectorData=ld.vectorData?{paths:JSON.parse(JSON.stringify(ld.vectorData.paths)),style:{...ld.vectorData.style}}:null;
      l._adjDirty=true; // Force recompute after restore
      l._adjCacheParams=null; // Invalidate Canvas2D adj cache so it recomputes with restored params
      l._adjPreviewParams=null; // Clear any stale preview (transient, never part of a snapshot)
      if(l.type==="vector"&&l.vectorData)renderVectorLayer(l); // Re-rasterize vector after restore
    }
    state.act=Math.min(sn.act,state.layers.length-1);
    if(sn.sel){setSel({...sn.sel});}else{clearSel();};
    state.tfm=null;
    // Notify React if canvas dimensions changed
    if((oldW!==sn.w||oldH!==sn.h)&&state.onDimsChanged)state.onDimsChanged(sn.w,sn.h);
    // Mark all layers GPU-dirty and undo-dirty after restore
    for(const l of state.layers){l._glDirty=true;l._undoClean=false;}
    // Invalidate last snap cache since layer data was externally replaced
    _prevSnapshotLayers=null;
  };
  const pushUndo = (label) => {
    state.undo.push(takeSnapshot());
    if (state.undo.length > 30) state.undo.shift();
    state.redo = [];
    if (label) state.history.push({ icon: "⚙", label });
    state.dirty = true;
    for (const l of state.layers) {
      if (l.type === "adjustment") l._adjDirty = true;
      l._undoClean = false;
    }
  };


  // ── Selection mask helpers ──
  // Selection is now a full alpha mask: Uint8Array of w*h, 0=unselected, 255=selected
  // state.sel = { mask: Uint8Array(w*h), x,y,w,h (bounding box), t (source type), gen (generation) } or null
  let _selGen=0;
  function setSel(sel){state.sel=sel;if(sel)sel.gen=++_selGen;state._selGen=_selGen;}
  function clearSel(){state.sel=null;state._selGen=++_selGen;}

  function selFromRect(rx,ry,rw,rh){
    const m=new Uint8Array(state.w*state.h);
    const x0=Math.max(0,Math.round(rx)),y0=Math.max(0,Math.round(ry));
    const x1=Math.min(state.w,Math.round(rx+rw)),y1=Math.min(state.h,Math.round(ry+rh));
    for(let y=y0;y<y1;y++)for(let x=x0;x<x1;x++)m[y*state.w+x]=255;
    return{mask:m,x:x0,y:y0,w:x1-x0,h:y1-y0,t:"rect"};
  }

  function selFromEllipse(rx,ry,rw,rh){
    const m=new Uint8Array(state.w*state.h);
    const cx2=rx+rw/2,cy2=ry+rh/2,radX=Math.abs(rw/2),radY=Math.abs(rh/2);
    const x0=Math.max(0,Math.round(rx)-1),y0=Math.max(0,Math.round(ry)-1);
    const x1=Math.min(state.w,Math.round(rx+rw)+1),y1=Math.min(state.h,Math.round(ry+rh)+1);
    for(let y=y0;y<y1;y++)for(let x=x0;x<x1;x++){
      const dx=(x-cx2)/radX,dy=(y-cy2)/radY;
      const dist=Math.sqrt(dx*dx+dy*dy);
      if(dist<=1){
        // Anti-alias: fade at the edge over ~1.2px
        const aa=Math.min(1,Math.max(0,(1-dist)*Math.min(radX,radY)*1.2));
        m[y*state.w+x]=Math.round(aa*255);
      }
    }
    return{mask:m,x:Math.max(0,Math.round(rx)),y:Math.max(0,Math.round(ry)),w:Math.min(state.w,Math.round(rx+rw))-Math.max(0,Math.round(rx)),h:Math.min(state.h,Math.round(ry+rh))-Math.max(0,Math.round(ry)),t:"ellipse"};
  }

  function selFromLasso(points){
    if(!points||points.length<3)return null;
    const m=new Uint8Array(state.w*state.h);
    // Scanline fill for polygon with anti-aliased edges
    let minX=state.w,minY=state.h,maxX=0,maxY=0;
    for(const p of points){minX=Math.min(minX,p.x);minY=Math.min(minY,p.y);maxX=Math.max(maxX,p.x);maxY=Math.max(maxY,p.y);}
    minX=Math.max(0,Math.floor(minX));minY=Math.max(0,Math.floor(minY));maxX=Math.min(state.w-1,Math.ceil(maxX));maxY=Math.min(state.h-1,Math.ceil(maxY));
    // Fill interior fully
    for(let y=minY;y<=maxY;y++){
      const nodes=[];const n=points.length;
      for(let i=0,j=n-1;i<n;j=i++){
        const yi=points[i].y,yj=points[j].y;
        if((yi<=y&&yj>y)||(yj<=y&&yi>y)){
          nodes.push(points[i].x+(y-yi)/(yj-yi)*(points[j].x-points[i].x));
        }
      }
      nodes.sort((a,b)=>a-b);
      for(let i=0;i<nodes.length;i+=2){
        if(i+1>=nodes.length)break;
        const leftEdge=nodes[i],rightEdge=nodes[i+1];
        const x0=Math.max(minX,Math.floor(leftEdge)),x1=Math.min(maxX,Math.ceil(rightEdge));
        for(let x=x0;x<=x1;x++){
          // Sub-pixel coverage at edges
          let cov=1;
          if(x<leftEdge+1)cov=Math.min(cov,x+1-leftEdge);
          if(x>rightEdge-1)cov=Math.min(cov,rightEdge-x);
          cov=Math.max(0,Math.min(1,cov));
          const val=Math.round(cov*255);
          m[y*state.w+x]=Math.max(m[y*state.w+x],val);
        }
      }
    }
    return{mask:m,x:minX,y:minY,w:maxX-minX+1,h:maxY-minY+1,t:"lasso"};
  }

  function selFromWand(sx,sy,tolerance){
    const L=state.layers[state.act];if(!L)return null;
    const id=L.x.getImageData(0,0,state.w,state.h).data;
    const m=new Uint8Array(state.w*state.h);
    const w=state.w,h=state.h;
    const si=(Math.round(sy)*w+Math.round(sx))*4;
    const tr=id[si],tg=id[si+1],tb=id[si+2];
    const visited=new Uint8Array(w*h);
    const stack=[[Math.round(sx),Math.round(sy)]];
    let minX=w,minY=h,maxX=0,maxY=0;
    const tol=tolerance*tolerance*3;
    while(stack.length){
      const[cx,cy]=stack.pop();
      if(cx<0||cx>=w||cy<0||cy>=h)continue;
      const idx=cy*w+cx;
      if(visited[idx])continue;
      visited[idx]=1;
      const pi=idx*4;
      const dr=id[pi]-tr,dg=id[pi+1]-tg,db=id[pi+2]-tb;
      if(dr*dr+dg*dg+db*db>tol)continue;
      m[idx]=255;
      minX=Math.min(minX,cx);minY=Math.min(minY,cy);maxX=Math.max(maxX,cx);maxY=Math.max(maxY,cy);
      stack.push([cx-1,cy],[cx+1,cy],[cx,cy-1],[cx,cy+1]);
    }
    if(maxX<minX)return null;
    return{mask:m,x:minX,y:minY,w:maxX-minX+1,h:maxY-minY+1,t:"wand"};
  }

  function selInvert(){
    if(!state.sel)return;
    const m=state.sel.mask;
    for(let i=0;i<m.length;i++)m[i]=255-m[i];
    // Recompute bbox
    let minX=state.w,minY=state.h,maxX=0,maxY=0;
    for(let y=0;y<state.h;y++)for(let x=0;x<state.w;x++){
      if(m[y*state.w+x]>0){minX=Math.min(minX,x);minY=Math.min(minY,y);maxX=Math.max(maxX,x);maxY=Math.max(maxY,y);}
    }
    state.sel.x=minX;state.sel.y=minY;state.sel.w=maxX-minX+1;state.sel.h=maxY-minY+1;state.sel.t="inverted";
  }

  function selFeather(radius){
    if(!state.sel||radius<1)return;
    const m=state.sel.mask,w=state.w,h=state.h;
    const copy=new Uint8Array(m);
    const r=Math.round(radius);
    // Gaussian-ish blur on the mask
    for(let y=0;y<h;y++)for(let x=0;x<w;x++){
      let sum=0,cnt=0;
      for(let dy=-r;dy<=r;dy++)for(let dx=-r;dx<=r;dx++){
        const nx=x+dx,ny=y+dy;
        if(nx<0||nx>=w||ny<0||ny>=h)continue;
        const dist=Math.sqrt(dx*dx+dy*dy);
        if(dist>r)continue;
        const wt=1-dist/r;
        sum+=copy[ny*w+nx]*wt;cnt+=wt;
      }
      m[y*w+x]=Math.round(sum/cnt);
    }
    state.sel.t="feathered";state.sel.gen=++_selGen;
  }

  // Merge (union) a new selection into the existing one
  function selMerge(newSel){
    if(!newSel)return;
    if(!state.sel){setSel(newSel);return;}
    const m=state.sel.mask,nm=newSel.mask;
    let minX=state.w,minY=state.h,maxX=0,maxY=0;
    for(let i=0;i<m.length;i++){
      m[i]=Math.max(m[i],nm[i]);
    }
    for(let y=0;y<state.h;y++)for(let x=0;x<state.w;x++){
      if(m[y*state.w+x]>0){minX=Math.min(minX,x);minY=Math.min(minY,y);maxX=Math.max(maxX,x);maxY=Math.max(maxY,y);}
    }
    state.sel.x=minX;state.sel.y=minY;state.sel.w=maxX-minX+1;state.sel.h=maxY-minY+1;state.sel.t="merged";state.sel.gen=++_selGen;
  }

  // Subtract a selection from the existing one
  function selSubtract(subSel){
    if(!subSel||!state.sel)return;
    const m=state.sel.mask,sm=subSel.mask;
    let minX=state.w,minY=state.h,maxX=0,maxY=0;
    let hasAny=false;
    for(let i=0;i<m.length;i++){
      m[i]=Math.max(0,m[i]-sm[i]);
    }
    for(let y=0;y<state.h;y++)for(let x=0;x<state.w;x++){
      if(m[y*state.w+x]>0){minX=Math.min(minX,x);minY=Math.min(minY,y);maxX=Math.max(maxX,x);maxY=Math.max(maxY,y);hasAny=true;}
    }
    if(!hasAny){clearSel();return;}
    state.sel.x=minX;state.sel.y=minY;state.sel.w=maxX-minX+1;state.sel.h=maxY-minY+1;state.sel.t="subtracted";state.sel.gen=++_selGen;
  }

  // Apply selection mask as clip to a canvas context
  // Cached — only rebuilds when selection mask identity changes
  let _cachedSelectionClip=null;
  function clipToSel(ctx){
    if(!state.sel)return;
    const m=state.sel.mask;
    const{canvas:tc,ctx:tx}=renderCache.getCanvas('clipSel',state.w,state.h);
    if(_cachedSelectionClip===m)return tc;
    const id=tx.createImageData(state.w,state.h);
    for(let i=0;i<m.length;i++){id.data[i*4]=255;id.data[i*4+1]=255;id.data[i*4+2]=255;id.data[i*4+3]=m[i];}
    tx.putImageData(id,0,0);
    _cachedSelectionClip=m;
    return tc;
  }

  // Copy pixels through selection mask
  function copyThroughMask(L){
    if(!state.sel||!L)return null;
    const src=L.x.getImageData(0,0,state.w,state.h);
    const dst=new ImageData(state.w,state.h);
    const m=state.sel.mask;
    for(let i=0;i<m.length;i++){
      const a=m[i]/255;
      dst.data[i*4]=src.data[i*4];
      dst.data[i*4+1]=src.data[i*4+1];
      dst.data[i*4+2]=src.data[i*4+2];
      dst.data[i*4+3]=Math.round(src.data[i*4+3]*a);
    }
    return{d:dst,w:state.w,h:state.h,full:true};
  }

  // Render text data onto a layer's canvas (called on every frame for text layers)
  function renderTextLayer(L){
    if(!L||!L.textData)return;
    const td=L.textData,cx=L.x;
    cx.clearRect(0,0,state.w,state.h);
    cx.font=`${td.weight||"normal"} ${td.size||32}px ${td.font||"sans-serif"}`;
    cx.fillStyle=td.color||"#000";
    cx.textAlign=td.align||"left";
    cx.textBaseline="top";
    const lines=(td.text||"").split("\n");
    const lh=(td.size||32)*1.3;
    lines.forEach((ln,i)=>cx.fillText(ln,td.x||20,td.y||20+i*lh));
  }

  // Rasterize vector layer paths onto its canvas
  function renderVectorLayer(L){
    if(!L||!L.vectorData)return;
    const vd=L.vectorData,cx=L.x,sty=vd.style||{};
    cx.clearRect(0,0,state.w,state.h);
    cx.save();
    cx.globalAlpha=sty.opacity??1;
    cx.lineCap="round";cx.lineJoin="round";
    for(const path of(vd.paths||[])){
      if(!path.points||path.points.length<2)continue;
      cx.beginPath();
      const pts=path.points;
      cx.moveTo(pts[0].x,pts[0].y);
      for(let i=1;i<pts.length;i++){
        const prev=pts[i-1],cur=pts[i];
        const c1x=prev.cp2x!=null?prev.cp2x:prev.x;
        const c1y=prev.cp2y!=null?prev.cp2y:prev.y;
        const c2x=cur.cp1x!=null?cur.cp1x:cur.x;
        const c2y=cur.cp1y!=null?cur.cp1y:cur.y;
        cx.bezierCurveTo(c1x,c1y,c2x,c2y,cur.x,cur.y);
      }
      if(path.closed)cx.closePath();
      if(sty.fillColor){cx.fillStyle=sty.fillColor;cx.fill();}
      if(sty.strokeColor){cx.strokeStyle=sty.strokeColor;cx.lineWidth=sty.strokeWidth||2;cx.stroke();}
    }
    cx.restore();
    L._vectorDirty=false;
  }

  // Clear pixels under selection mask
  function clearThroughMask(L){
    if(!state.sel||!L)return;
    const id=L.x.getImageData(0,0,state.w,state.h);
    const m=state.sel.mask;
    for(let i=0;i<m.length;i++){
      const a=m[i]/255;
      id.data[i*4+3]=Math.round(id.data[i*4+3]*(1-a));
    }
    L.x.putImageData(id,0,0);
  }

  // Brush tip generator — returns an OffscreenCanvas with the brush footprint
  // Cached for round/square tips (only regenerate when params change)
  let _cachedBrushTip=null,_cachedBrushTipKey="";
  function makeBrushTip(sz,hardness,tipType,r,g,b){
    // Scatter and texture tips use randomness — always regenerate
    if(tipType!=="scatter"&&tipType!=="texture"&&tipType!=="hair"&&tipType!=="grass"&&tipType!=="splatter"&&tipType!=="spray"){
      const key=`${sz}|${hardness}|${tipType}|${r}|${g}|${b}`;
      if(_cachedBrushTip&&_cachedBrushTipKey===key)return _cachedBrushTip;
      _cachedBrushTipKey=key;
    }
    const pad=2; // padding for anti-aliased edges
    const s2=Math.max(4,Math.ceil(sz)+pad*2);
    const tc=new OffscreenCanvas(s2,s2);const tx=tc.getContext("2d");
    tx.imageSmoothingEnabled=true;tx.imageSmoothingQuality="high";
    const cx2=s2/2,cy2=s2/2,rad=sz/2;

    if(tipType==="scatter"){
      tx.fillStyle=`rgb(${r},${g},${b})`;
      const count=Math.max(4,Math.round(sz*0.8));
      for(let i=0;i<count;i++){
        const angle=Math.random()*Math.PI*2;const dist=Math.random()*rad*0.9;
        const pr=1+Math.random()*2;
        tx.globalAlpha=0.3+Math.random()*0.7;
        tx.beginPath();tx.arc(cx2+Math.cos(angle)*dist,cy2+Math.sin(angle)*dist,pr,0,Math.PI*2);tx.fill();
      }
      tx.globalAlpha=1;
    } else if(tipType==="square"){
      const inset=rad*(1-hardness)*0.5;
      tx.fillStyle=`rgb(${r},${g},${b})`;
      tx.beginPath();
      tx.roundRect(cx2-rad+inset,cy2-rad+inset,sz-inset*2,sz-inset*2,(1-hardness)*rad*0.5);
      tx.fill();
    } else if(tipType==="texture"){
      const id=tx.createImageData(s2,s2);
      for(let py=0;py<s2;py++)for(let px=0;px<s2;px++){
        const dx=px-cx2,dy=py-cy2,dist=Math.sqrt(dx*dx+dy*dy)/rad;
        if(dist>1)continue;
        const falloff=1-Math.pow(dist,1/(hardness*2+0.1));
        const noise=Math.random()*0.5+0.5;
        const a=Math.round(falloff*noise*255);
        const idx=(py*s2+px)*4;
        id.data[idx]=r;id.data[idx+1]=g;id.data[idx+2]=b;id.data[idx+3]=a;
      }
      tx.putImageData(id,0,0);
    } else if(tipType==="flat"){
      // Flat/Calligraphic brush — elliptical with angle and soft edges
      const tiltAngle=(state.p?.tiltX||0)*Math.PI/180;
      const angle=Math.abs(tiltAngle)>0.01?tiltAngle:Math.PI/4;
      const roundness=0.4; // width-to-height ratio
      tx.save();tx.translate(cx2,cy2);tx.rotate(angle);
      // Soft edge via radial gradient stretched into ellipse
      const grd=tx.createRadialGradient(0,0,rad*0.3*hardness,0,0,rad);
      grd.addColorStop(0,`rgba(${r},${g},${b},1)`);
      grd.addColorStop(hardness>0.8?0.9:0.6,`rgba(${r},${g},${b},${hardness>0.8?1:0.7})`);
      grd.addColorStop(1,`rgba(${r},${g},${b},0)`);
      tx.scale(1,roundness);
      tx.fillStyle=grd;
      tx.beginPath();tx.arc(0,0,rad,0,Math.PI*2);tx.fill();
      tx.restore();
    } else if(tipType==="hair"){
      // Hair/Fur — thin strands from center
      const count=8+Math.round(Math.random()*12);
      tx.strokeStyle=`rgb(${r},${g},${b})`;tx.lineCap="round";
      for(let i=0;i<count;i++){
        const angle=Math.random()*Math.PI*2;const len=rad*(0.5+Math.random()*0.5);
        const curl=(Math.random()-0.5)*0.6;
        tx.globalAlpha=0.4+Math.random()*0.6;tx.lineWidth=0.5+Math.random()*1.5;
        tx.beginPath();tx.moveTo(cx2,cy2);
        tx.quadraticCurveTo(cx2+Math.cos(angle+curl)*len*0.6,cy2+Math.sin(angle+curl)*len*0.6,cx2+Math.cos(angle)*len,cy2+Math.sin(angle)*len);
        tx.stroke();
      }
      tx.globalAlpha=1;
    } else if(tipType==="grass"){
      // Grass — upward tapered strokes
      const blades=3+Math.round(Math.random()*5);
      tx.strokeStyle=`rgb(${r},${g},${b})`;tx.lineCap="round";
      for(let i=0;i<blades;i++){
        const bx2=cx2+(Math.random()-0.5)*rad*0.8;const lean=(Math.random()-0.5)*0.8;
        const h2=rad*(0.6+Math.random()*0.8);
        tx.globalAlpha=0.5+Math.random()*0.5;tx.lineWidth=1+Math.random()*2;
        tx.beginPath();tx.moveTo(bx2,cy2+rad*0.4);
        tx.quadraticCurveTo(bx2+lean*h2*0.4,cy2+rad*0.4-h2*0.6,bx2+lean*h2*0.3,cy2+rad*0.4-h2);
        tx.stroke();
      }
      tx.globalAlpha=1;
    } else if(tipType==="splatter"){
      // Splatter/Ink — irregular blob with satellite dots
      tx.fillStyle=`rgb(${r},${g},${b})`;tx.globalAlpha=0.8;
      tx.beginPath();
      for(let a=0;a<Math.PI*2;a+=0.3){
        const rr=rad*(0.3+Math.random()*0.4);
        const px2=cx2+Math.cos(a)*rr,py2=cy2+Math.sin(a)*rr;
        a===0?tx.moveTo(px2,py2):tx.lineTo(px2,py2);
      }
      tx.closePath();tx.fill();
      const dots=5+Math.round(Math.random()*10);
      for(let i=0;i<dots;i++){
        const angle=Math.random()*Math.PI*2;const dist=rad*(0.5+Math.random()*0.5);
        tx.globalAlpha=0.3+Math.random()*0.5;
        tx.beginPath();tx.arc(cx2+Math.cos(angle)*dist,cy2+Math.sin(angle)*dist,0.5+Math.random()*2,0,Math.PI*2);tx.fill();
      }
      tx.globalAlpha=1;
    } else if(tipType==="spray"){
      // Spray/Airbrush — gaussian-distributed random dots
      tx.fillStyle=`rgb(${r},${g},${b})`;
      const count=Math.max(20,Math.round(sz*3));
      for(let i=0;i<count;i++){
        const u1=Math.random(),u2=Math.random();
        const g1=Math.sqrt(-2*Math.log(u1||0.001))*Math.cos(2*Math.PI*u2);
        const g2=Math.sqrt(-2*Math.log(u1||0.001))*Math.sin(2*Math.PI*u2);
        const px2=cx2+g1*rad*0.35,py2=cy2+g2*rad*0.35;
        const dist=Math.sqrt((px2-cx2)**2+(py2-cy2)**2)/rad;
        if(dist>1)continue;
        tx.globalAlpha=Math.max(0.05,(1-dist)*0.3);
        tx.fillRect(px2,py2,1,1);
      }
      tx.globalAlpha=1;
    } else {
      // Round brush — always use radial gradient for smooth anti-aliased edges
      // Inner radius determines hardness (larger = harder edge)
      const innerR=rad*Math.max(0,hardness-0.05);
      const grad=tx.createRadialGradient(cx2,cy2,innerR,cx2,cy2,rad);
      grad.addColorStop(0,`rgba(${r},${g},${b},1)`);
      // For very hard brushes, add a near-opaque stop close to the edge
      if(hardness>=0.9){
        grad.addColorStop(0.85,`rgba(${r},${g},${b},1)`);
        grad.addColorStop(1,`rgba(${r},${g},${b},0)`);
      } else {
        grad.addColorStop(1,`rgba(${r},${g},${b},0)`);
      }
      tx.fillStyle=grad;
      tx.beginPath();tx.arc(cx2,cy2,rad+0.5,0,Math.PI*2);tx.fill();
    }
    // Store the effective radius offset for proper positioning
    tc._pad=pad;
    if(tipType!=="scatter"&&tipType!=="texture"&&tipType!=="hair"&&tipType!=="grass"&&tipType!=="splatter"&&tipType!=="spray")_cachedBrushTip=tc;
    return tc;
  }

  // ── Catmull-Rom spline interpolation for smooth brush strokes (1.1) ──
  // Ring buffer of the last 4 pointer positions for spline calculation
  if(!state._ptRing)state._ptRing=[];

  // Catmull-Rom: compute a point on the spline through p0,p1,p2,p3 at parameter t (0..1)
  function catmullRom(p0,p1,p2,p3,t){
    const t2=t*t,t3=t2*t;
    return{
      x:0.5*((2*p1.x)+(-p0.x+p2.x)*t+(2*p0.x-5*p1.x+4*p2.x-p3.x)*t2+(-p0.x+3*p1.x-3*p2.x+p3.x)*t3),
      y:0.5*((2*p1.y)+(-p0.y+p2.y)*t+(2*p0.y-5*p1.y+4*p2.y-p3.y)*t2+(-p0.y+3*p1.y-3*p2.y+p3.y)*t3)
    };
  }

  // Shared stamp interpolation with dynamics (3.1, 3.2)
  function interpolateStamps(ctx,btip,bx,by,sz,bpad){
    if(!state.lastPt)return;
    const dx=bx-state.lastPt.x,dy=by-state.lastPt.y;
    const dist=Math.sqrt(dx*dx+dy*dy);
    // Configurable spacing (3.1) — use tighter default for smooth brushes
    const tipName=TIP_NAMES[state.p.brushTip]||"round";
    const isSmooth=tipName==="round"||tipName==="square"||tipName==="flat";
    const defaultSpacing=isSmooth?10:25; // smooth brushes need tight spacing
    const spacingPct=Math.max(1,(state.p.brushSpacing||defaultSpacing))/100;
    const spacing=Math.max(1,sz*spacingPct);
    if(dist<=spacing)return;
    const steps=Math.min(256,Math.ceil(dist/spacing));
    // Dynamics params (3.2)
    const szJit=state.p.sizeJitter||0;
    const angJit=state.p.angleJitter||0;
    const opJit=state.p.opacityJitter||0;
    const scatter=state.p.scatterAmount||0;
    const flow=state.p.brushFlow!=null?state.p.brushFlow:1;
    const hasDynamics=szJit>0||angJit>0||opJit>0||scatter>0;
    const strokeAngle=Math.atan2(dy,dx);
    const perpX=Math.cos(strokeAngle+Math.PI/2);
    const perpY=Math.sin(strokeAngle+Math.PI/2);

    for(let i=1;i<steps;i++){
      const t=i/steps;
      let ix=state.lastPt.x+dx*t,iy=state.lastPt.y+dy*t;
      // Scatter
      if(scatter>0){
        const off=(Math.random()-0.5)*2*scatter*sz;
        ix+=perpX*off;iy+=perpY*off;
      }
      if(hasDynamics||flow<1){
        ctx.save();
        const opMul=(1-opJit*Math.random())*flow;
        if(opMul<1)ctx.globalAlpha=(ctx.globalAlpha||1)*opMul;
        if(szJit>0||angJit>0){
          const jSz=sz*(1-szJit*Math.random());
          const jTip=makeBrushTip(jSz,state.p.brushHardness??0.8,TIP_NAMES[state.p.brushTip]||"round",state.p.brushR||0,state.p.brushG||0,state.p.brushB||0);
          const jPad=jTip._pad||0;
          if(angJit>0){
            ctx.translate(ix,iy);ctx.rotate(angJit*Math.random()*Math.PI*2);
            ctx.drawImage(jTip,-jSz/2-jPad,-jSz/2-jPad);
          }else{
            ctx.drawImage(jTip,ix-jSz/2-jPad,iy-jSz/2-jPad);
          }
        }else{
          ctx.drawImage(btip,ix-sz/2-bpad,iy-sz/2-bpad);
        }
        ctx.restore();
      }else{
        ctx.drawImage(btip,ix-sz/2-bpad,iy-sz/2-bpad);
      }
    }
  }

  function brush(){
    const L=state.layers[state.act];if(!L?.vis){state.onHiddenLayerAttempt?.();return;}
    const cx=L.x,{cursorX:bx,cursorY:by,brushSize:baseSz,brushOpacity:baseOp,brushR:r,brushG:g,brushB:b,activeTool:tl,brushHardness:baseHard,brushTip:tipIdx,pressure:rawPrs,pressureSize:pSz,pressureOpacity:pOp,pressureHardness:pHard,pressureFlow:pFlow,pressureCurve:pCurve,brushFlow:baseFlow}=state.p;
    const tipType=TIP_NAMES[tipIdx]||"round";
    // Apply pressure curve (3.5) — power function remapping
    const curve=pCurve||1;
    const prs=rawPrs>0?Math.pow(rawPrs,curve):1;
    // Apply pressure modulations
    const sz=pSz?baseSz*prs:baseSz;
    const op=pOp?baseOp*prs:baseOp;
    const hardness=pHard?(baseHard??0.8)*prs:(baseHard??0.8);
    const flow=pFlow?(baseFlow||1)*prs:(baseFlow||1);

    // Dodge tool — lighten pixels
    if(tl===T.DODGE){
      const rad=sz/2;const ox=Math.max(0,Math.round(bx-rad)),oy=Math.max(0,Math.round(by-rad));
      const rw=Math.min(Math.ceil(sz),state.w-ox),rh=Math.min(Math.ceil(sz),state.h-oy);
      if(rw<1||rh<1){state.lastPt={x:bx,y:by};return;}
      const id=cx.getImageData(ox,oy,rw,rh);const d=id.data;
      const selM=state.sel?state.sel.mask:null;
      for(let py=0;py<id.height;py++)for(let px=0;px<id.width;px++){
        if(selM&&selM[(py+oy)*state.w+(px+ox)]<128)continue;
        const dx2=(px+ox)-bx,dy2=(py+oy)-by,dist=Math.sqrt(dx2*dx2+dy2*dy2);
        if(dist>rad)continue;const falloff=(1-dist/rad)*op*0.15*(hardness+0.2);
        const i=(py*id.width+px)*4;d[i]=Math.min(255,d[i]+falloff*60);d[i+1]=Math.min(255,d[i+1]+falloff*60);d[i+2]=Math.min(255,d[i+2]+falloff*60);
      }
      cx.putImageData(id,ox,oy);state.lastPt={x:bx,y:by};return;
    }
    // Burn tool — darken pixels
    if(tl===T.BURN){
      const rad=sz/2;const ox=Math.max(0,Math.round(bx-rad)),oy=Math.max(0,Math.round(by-rad));
      const rw=Math.min(Math.ceil(sz),state.w-ox),rh=Math.min(Math.ceil(sz),state.h-oy);
      if(rw<1||rh<1){state.lastPt={x:bx,y:by};return;}
      const id=cx.getImageData(ox,oy,rw,rh);const d=id.data;
      const selM=state.sel?state.sel.mask:null;
      for(let py=0;py<id.height;py++)for(let px=0;px<id.width;px++){
        if(selM&&selM[(py+oy)*state.w+(px+ox)]<128)continue;
        const dx2=(px+ox)-bx,dy2=(py+oy)-by,dist=Math.sqrt(dx2*dx2+dy2*dy2);
        if(dist>rad)continue;const falloff=(1-dist/rad)*op*0.15*(hardness+0.2);
        const i=(py*id.width+px)*4;d[i]=Math.max(0,d[i]-falloff*60);d[i+1]=Math.max(0,d[i+1]-falloff*60);d[i+2]=Math.max(0,d[i+2]-falloff*60);
      }
      cx.putImageData(id,ox,oy);state.lastPt={x:bx,y:by};return;
    }
    // Distort/Smudge tool — push pixels in drag direction
    if(tl===T.DISTORT&&state.lastPt){
      const rad=sz/2;const ddx=bx-state.lastPt.x,ddy=by-state.lastPt.y;
      if(Math.abs(ddx)<0.5&&Math.abs(ddy)<0.5){state.lastPt={x:bx,y:by};return;}
      const x0=Math.max(0,Math.round(bx-rad)),y0=Math.max(0,Math.round(by-rad));
      const rw=Math.min(Math.ceil(sz),state.w-x0),rh=Math.min(Math.ceil(sz),state.h-y0);
      if(rw<1||rh<1){state.lastPt={x:bx,y:by};return;}
      const id=cx.getImageData(x0,y0,rw,rh);const copy=new Uint8ClampedArray(id.data);
      for(let py=0;py<rh;py++)for(let px=0;px<rw;px++){
        const dx2=(px+x0)-bx,dy2=(py+y0)-by,dist=Math.sqrt(dx2*dx2+dy2*dy2);
        if(dist>rad)continue;const str=(1-dist/rad)*op*0.6;
        const sx3=Math.round(px-ddx*str),sy3=Math.round(py-ddy*str);
        if(sx3>=0&&sx3<rw&&sy3>=0&&sy3<rh){const si=(sy3*rw+sx3)*4,di=(py*rw+px)*4;id.data[di]=copy[si];id.data[di+1]=copy[si+1];id.data[di+2]=copy[si+2];id.data[di+3]=copy[si+3];}
      }
      cx.putImageData(id,x0,y0);state.lastPt={x:bx,y:by};return;
    }
    if(tl===T.DISTORT){state.lastPt={x:bx,y:by};return;}
    // Rubber stamp tool — clone from source offset
    if(tl===T.RUBBER_STAMP){
      if(!state.stampSrc)return;
      // Source offset: where we sample = cursor + (stampSrc - stampFirstClick)
      const offX=state.stampSrc.offX||0, offY=state.stampSrc.offY||0;
      const srcX=bx+offX, srcY=by+offY;
      // Build composite to sample from
      if(!state.stampComp){state.stampComp=new OffscreenCanvas(state.w,state.h);}
      if(state.stampComp.width!==state.w)state.stampComp.width=state.w;
      if(state.stampComp.height!==state.h)state.stampComp.height=state.h;
      if(!state.stampCompDirty){
        const cc=state.stampComp.getContext("2d",{willReadFrequently:true});cc.clearRect(0,0,state.w,state.h);
        for(const ll of state.layers){if(!ll.vis)continue;cc.globalAlpha=ll.op;cc.drawImage(ll.c,0,0);}
        cc.globalAlpha=1;state.stampCompDirty=true;
      }
      // Paint a circular stamp of pixels from source to destination
      const rad=sz/2;
      cx.save();cx.globalAlpha=op;
      if(state.sel){cx.beginPath();cx.rect(state.sel.x,state.sel.y,state.sel.w,state.sel.h);cx.clip();}
      // Draw the source region centered on srcX,srcY into a circle at bx,by
      // Soft stamp: draw through radial gradient mask for hardness
      // Reuse cached stamp canvas to avoid per-dab allocation
      const stampSz=Math.ceil(sz);
      const{canvas:stmp,ctx:stx}=renderCache.getCanvas('stampTmp',stampSz,stampSz);
      stx.clearRect(0,0,stampSz,stampSz);
      stx.globalCompositeOperation="source-over";
      stx.drawImage(state.stampComp,srcX-rad,srcY-rad,sz,sz,0,0,sz,sz);
      // Apply softness mask
      stx.globalCompositeOperation="destination-in";
      const sg=stx.createRadialGradient(sz/2,sz/2,rad*hardness*0.5,sz/2,sz/2,rad);
      sg.addColorStop(0,"rgba(255,255,255,1)");sg.addColorStop(1,hardness>=0.95?"rgba(255,255,255,1)":"rgba(255,255,255,0)");
      stx.fillStyle=sg;stx.fillRect(0,0,sz,sz);
      stx.globalCompositeOperation="source-over";
      cx.drawImage(stmp,bx-rad,by-rad);
      cx.restore();
      // Interpolate for smooth strokes
      if(state.lastPt){
        const ddx=bx-state.lastPt.x,ddy=by-state.lastPt.y,dist=Math.sqrt(ddx*ddx+ddy*ddy);
        const sp=Math.max(2,sz*0.3);
        if(dist>sp){const steps=Math.ceil(dist/sp);for(let i=1;i<steps;i++){
          const tt=i/steps,ix=state.lastPt.x+ddx*tt,iy=state.lastPt.y+ddy*tt;
          const sx2=ix+offX,sy2=iy+offY;
          cx.save();cx.globalAlpha=op;
          if(state.sel){cx.beginPath();cx.rect(state.sel.x,state.sel.y,state.sel.w,state.sel.h);cx.clip();}
          stx.clearRect(0,0,stampSz,stampSz);
          stx.globalCompositeOperation="source-over";
          stx.drawImage(state.stampComp,sx2-rad,sy2-rad,sz,sz,0,0,sz,sz);
          stx.globalCompositeOperation="destination-in";
          const sg2=stx.createRadialGradient(sz/2,sz/2,rad*hardness*0.5,sz/2,sz/2,rad);
          sg2.addColorStop(0,"rgba(255,255,255,1)");sg2.addColorStop(1,hardness>=0.95?"rgba(255,255,255,1)":"rgba(255,255,255,0)");
          stx.fillStyle=sg2;stx.fillRect(0,0,sz,sz);
          cx.drawImage(stmp,ix-rad,iy-rad);
          cx.restore();
        }}
      }
      state.lastPt={x:bx,y:by};
      return;
    }

    // Draw stamps directly to layer canvas (immediate visual feedback)
    cx.save();cx.globalAlpha=op;
    if(state.sel&&state.sel.mask){
      // ── Selection-constrained brush ──
      const sel=state.sel;
      const isSimpleRect=sel.t==="rect"||sel.t==="all";
      if(isSimpleRect){
        if(tl===T.ERASER)cx.globalCompositeOperation="destination-out";
        cx.beginPath();cx.rect(sel.x,sel.y,sel.w,sel.h);cx.clip();
        const isEraser=tl===T.ERASER;
        const btip=makeBrushTip(sz,hardness,isEraser?"round":tipType,isEraser?255:r,isEraser?255:g,isEraser?255:b);
        const bpad=btip._pad||0;
        cx.drawImage(btip,bx-sz/2-bpad,by-sz/2-bpad);
        interpolateStamps(cx,btip,bx,by,sz,bpad);
      } else {
      const{canvas:tc,ctx:tx}=renderCache.getCanvas('brushSel',state.w,state.h);
      tx.clearRect(0,0,state.w,state.h);
      tx.imageSmoothingEnabled=true;tx.imageSmoothingQuality="high";
      if(tl===T.ERASER)tx.globalCompositeOperation="destination-out";
      const isEraser=tl===T.ERASER;
      const btip=makeBrushTip(sz,hardness,isEraser?"round":tipType,isEraser?255:r,isEraser?255:g,isEraser?255:b);
      const bpad=btip._pad||0;
      tx.drawImage(btip,bx-sz/2-bpad,by-sz/2-bpad);
      interpolateStamps(tx,btip,bx,by,sz,bpad);
      const selMask=clipToSel(cx);
      tx.globalCompositeOperation="destination-in";
      tx.drawImage(selMask,0,0);
      tx.globalCompositeOperation="source-over";
      if(isEraser){cx.globalCompositeOperation="destination-out";cx.drawImage(tc,0,0);}
      else cx.drawImage(tc,0,0);
      }
    } else {
    if(tl===T.ERASER)cx.globalCompositeOperation="destination-out";

    const isEraser=tl===T.ERASER;
    const btip=makeBrushTip(sz,hardness,isEraser?"round":tipType,isEraser?255:r,isEraser?255:g,isEraser?255:b);
    const bpad=btip._pad||0;
    cx.drawImage(btip,bx-sz/2-bpad,by-sz/2-bpad);
    interpolateStamps(cx,btip,bx,by,sz,bpad);
    }
    cx.restore();
    state.lastPt={x:bx,y:by};
  }

  // Spot heal at a single point (used by both command and continuous drag)
  function spotHealAt(x,y,size){
    const L=state.layers[state.act];if(!L||!L.vis)return;
    const cx=L.x;
    const rad=Math.round(size/2);
    const margin=Math.ceil(rad*1.5);
    const x0=Math.max(0,Math.round(x)-margin),y0=Math.max(0,Math.round(y)-margin);
    const x1=Math.min(state.w,Math.round(x)+margin),y1=Math.min(state.h,Math.round(y)+margin);
    const rw=x1-x0,rh=y1-y0;
    if(rw<2||rh<2)return;
    const id=cx.getImageData(x0,y0,rw,rh);const d=id.data;
    const cx2=Math.round(x),cy2=Math.round(y);
    for(let py=0;py<rh;py++)for(let px=0;px<rw;px++){
      const dx2=(px+x0)-cx2,dy2=(py+y0)-cy2;
      const dist=Math.sqrt(dx2*dx2+dy2*dy2);
      if(dist>rad)continue;
      let sr=0,sg=0,sb=0,sw=0;
      const sampleRad=rad+4;
      for(let a=0;a<Math.PI*2;a+=Math.PI/8){
        const sx=Math.round(px+Math.cos(a)*sampleRad);
        const sy=Math.round(py+Math.sin(a)*sampleRad);
        if(sx<0||sx>=rw||sy<0||sy>=rh)continue;
        const si=(sy*rw+sx)*4;const w=1;
        sr+=d[si]*w;sg+=d[si+1]*w;sb+=d[si+2]*w;sw+=w;
      }
      if(sw>0){
        const blend=1-dist/rad;
        const i=(py*rw+px)*4;
        d[i]=Math.round(d[i]*(1-blend)+sr/sw*blend);
        d[i+1]=Math.round(d[i+1]*(1-blend)+sg/sw*blend);
        d[i+2]=Math.round(d[i+2]*(1-blend)+sb/sw*blend);
      }
    }
    cx.putImageData(id,x0,y0);
    state.lastPt={x,y};
  }

  // Fill tool — solid or gradient
  function floodFill(x,y,mode,r,g,b,r2,g2,b2,op,tolerance,x2,y2,stops){
    const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();return;}pushUndo("Fill");
    const cx=L.x;
    cx.save();cx.globalAlpha=op;

    if(mode==="gradient-linear"||mode==="gradient-radial"){
      const gw=state.w,gh=state.h;
      const tc=new OffscreenCanvas(gw,gh);const gx=tc.getContext("2d",{willReadFrequently:true});
      let grad;
      // Use explicit end point if provided, else default to opposite corner
      const ex2=x2!=null?x2:gw-x,ey2=y2!=null?y2:gh-y;
      if(mode==="gradient-radial"){
        const maxR=Math.sqrt((ex2-x)**2+(ey2-y)**2)||Math.max(gw,gh);
        grad=gx.createRadialGradient(x,y,0,x,y,maxR);
      } else {
        grad=gx.createLinearGradient(x,y,ex2,ey2);
      }
      // Multi-stop gradient support
      if(stops&&stops.length>=2){
        for(const st of stops)grad.addColorStop(st.pos,st.color);
      } else {
        grad.addColorStop(0,`rgb(${r},${g},${b})`);
        grad.addColorStop(1,`rgb(${r2},${g2},${b2})`);
      }
      gx.fillStyle=grad;gx.fillRect(0,0,gw,gh);
      // Apply through selection mask if active
      if(state.sel&&state.sel.mask){
        const gid=gx.getImageData(0,0,gw,gh);const gd=gid.data;const m=state.sel.mask;
        for(let i=0;i<m.length;i++){gd[i*4+3]=Math.round(gd[i*4+3]*(m[i]/255));}
        gx.putImageData(gid,0,0);
      }
      cx.drawImage(tc,0,0);
    } else {
      // Solid fill — flood fill from click point
      const id=cx.getImageData(0,0,state.w,state.h);const d=id.data;
      const w=state.w,h=state.h;
      const si=(Math.round(y)*w+Math.round(x))*4;
      const tr=d[si],tg=d[si+1],tb=d[si+2],ta=d[si+3];
      // If clicking on same color, just fill whole region
      if(tr===r&&tg===g&&tb===b)return cx.restore();
      const visited=new Uint8Array(w*h);
      const stack=[[Math.round(x),Math.round(y)]];
      const tol=(tolerance||30)*(tolerance||30)*3;
      while(stack.length){
        const[cx2,cy2]=stack.pop();
        if(cx2<0||cx2>=w||cy2<0||cy2>=h)continue;
        const idx=cy2*w+cx2;
        if(visited[idx])continue;
        // Check selection mask
        if(state.sel&&state.sel.mask[idx]<128)continue;
        visited[idx]=1;
        const pi=idx*4;
        const dr2=d[pi]-tr,dg=d[pi+1]-tg,db=d[pi+2]-tb;
        if(dr2*dr2+dg*dg+db*db>tol)continue;
        d[pi]=r;d[pi+1]=g;d[pi+2]=b;d[pi+3]=255;
        stack.push([cx2-1,cy2],[cx2+1,cy2],[cx2,cy2-1],[cx2,cy2+1]);
      }
      cx.putImageData(id,0,0);
    }
    cx.restore();
    state.history.push({icon:"🪣",label:mode==="solid"?"Fill":"Gradient"});
  }

  function filter(fid){
    const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();return;}pushUndo("Filter");
    const id=L.x.getImageData(0,0,state.w,state.h),d=id.data,w=state.w,h=state.h;
    const K=state._bridge;
    // Use KernelRegistry.run() if available — handles wasm→JS fallback internally
    if(K&&typeof K.run==='function'){
      if(fid===FLT.GRAY)K.run("grayscale",d,w,h);
      else if(fid===FLT.INVERT)K.run("invert",d,w,h);
      else if(fid===FLT.SEPIA)K.run("sepia",d,w,h);
      else if(fid===FLT.SHARP)K.run("sharpen",d,w,h);
      else if(fid===FLT.BLUR)K.run("gaussian_blur",d,w,h,{radius:2});
      else _filterJS(fid,d,w,h); // vignette, noise, auto — no kernel yet
    } else if(K&&K._wasmAvailable){
      // Legacy WasmBridge path
      if(fid===FLT.GRAY)K.runKernel("grayscale",d,w,h);
      else if(fid===FLT.INVERT)K.runKernel("invert",d,w,h);
      else if(fid===FLT.SEPIA)K.runKernel("sepia",d,w,h);
      else if(fid===FLT.SHARP)K.runKernel("sharpen",d,w,h);
      else if(fid===FLT.BLUR)K.runKernel("gaussian_blur",d,w,h,{radius:2});
      else _filterJS(fid,d,w,h);
    } else {
      _filterJS(fid,d,w,h);
    }
    L.x.putImageData(id,0,0);
  }
  // JS fallback for filters without wasm kernels (vignette, noise, auto)
  function _filterJS(fid,d,w,h){
    if(fid===FLT.GRAY)for(let i=0;i<d.length;i+=4){const v=d[i]*.3+d[i+1]*.59+d[i+2]*.11;d[i]=d[i+1]=d[i+2]=v;}
    else if(fid===FLT.INVERT)for(let i=0;i<d.length;i+=4){d[i]=255-d[i];d[i+1]=255-d[i+1];d[i+2]=255-d[i+2];}
    else if(fid===FLT.SEPIA)for(let i=0;i<d.length;i+=4){const r=d[i],g=d[i+1],b=d[i+2];d[i]=Math.min(255,r*.393+g*.769+b*.189);d[i+1]=Math.min(255,r*.349+g*.686+b*.168);d[i+2]=Math.min(255,r*.272+g*.534+b*.131);}
    else if(fid===FLT.SHARP){const src=new Uint8ClampedArray(d),k=[0,-1,0,-1,5,-1,0,-1,0];for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++)for(let c=0;c<3;c++){let v=0;for(let ky=-1;ky<=1;ky++)for(let kx=-1;kx<=1;kx++)v+=src[((y+ky)*w+(x+kx))*4+c]*k[(ky+1)*3+(kx+1)];d[(y*w+x)*4+c]=Math.min(255,Math.max(0,v));}}
    else if(fid===FLT.BLUR){const src=new Uint8ClampedArray(d),rad=2;for(let y=0;y<h;y++)for(let x=0;x<w;x++){let r=0,g=0,b=0,a=0,cnt=0;for(let dy=-rad;dy<=rad;dy++)for(let dx=-rad;dx<=rad;dx++){const nx=x+dx,ny=y+dy;if(nx<0||nx>=w||ny<0||ny>=h)continue;const si=(ny*w+nx)*4;r+=src[si];g+=src[si+1];b+=src[si+2];a+=src[si+3];cnt++;}const di=(y*w+x)*4;d[di]=r/cnt;d[di+1]=g/cnt;d[di+2]=b/cnt;d[di+3]=a/cnt;}}
    else if(fid===FLT.VIGNETTE){const cx2=w/2,cy2=h/2,maxD=Math.sqrt(cx2*cx2+cy2*cy2);for(let y=0;y<h;y++)for(let x=0;x<w;x++){const dist=Math.sqrt((x-cx2)**2+(y-cy2)**2)/maxD;const fade=1-dist*dist*0.85;const di=(y*w+x)*4;d[di]*=fade;d[di+1]*=fade;d[di+2]*=fade;}}
    else if(fid===FLT.NOISE){for(let i=0;i<d.length;i+=4){const n=(Math.random()-0.5)*40;d[i]=Math.max(0,Math.min(255,d[i]+n));d[i+1]=Math.max(0,Math.min(255,d[i+1]+n));d[i+2]=Math.max(0,Math.min(255,d[i+2]+n));}}
    else if(fid===FLT.AUTO){let minR=255,minG=255,minB=255,maxR=0,maxG=0,maxB=0;for(let i=0;i<d.length;i+=4){if(d[i+3]<10)continue;minR=Math.min(minR,d[i]);maxR=Math.max(maxR,d[i]);minG=Math.min(minG,d[i+1]);maxG=Math.max(maxG,d[i+1]);minB=Math.min(minB,d[i+2]);maxB=Math.max(maxB,d[i+2]);}const rR=maxR-minR||1,rG=maxG-minG||1,rB=maxB-minB||1;for(let i=0;i<d.length;i+=4){d[i]=(d[i]-minR)/rR*255;d[i+1]=(d[i+1]-minG)/rG*255;d[i+2]=(d[i+2]-minB)/rB*255;}}
    // Phase 5 filters
    else if(fid===FLT.EMBOSS){
      const src=new Uint8ClampedArray(d);
      const k=[-2,-1,0,-1,1,1,0,1,2];
      for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++)for(let c=0;c<3;c++){
        let v=0;for(let ky=-1;ky<=1;ky++)for(let kx=-1;kx<=1;kx++)v+=src[((y+ky)*w+(x+kx))*4+c]*k[(ky+1)*3+(kx+1)];
        d[(y*w+x)*4+c]=Math.min(255,Math.max(0,v+128));
      }
    }
    else if(fid===FLT.EDGE){
      const src=new Uint8ClampedArray(d);
      const gx=[-1,0,1,-2,0,2,-1,0,1],gy=[-1,-2,-1,0,0,0,1,2,1];
      for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
        let sumX=0,sumY=0;
        for(let ky=-1;ky<=1;ky++)for(let kx=-1;kx<=1;kx++){
          const si=((y+ky)*w+(x+kx))*4;
          const lum=src[si]*0.3+src[si+1]*0.59+src[si+2]*0.11;
          sumX+=lum*gx[(ky+1)*3+(kx+1)];sumY+=lum*gy[(ky+1)*3+(kx+1)];
        }
        const mag=Math.min(255,Math.sqrt(sumX*sumX+sumY*sumY));
        const di=(y*w+x)*4;d[di]=d[di+1]=d[di+2]=mag;
      }
    }
    else if(fid===FLT.POSTERIZE){
      const levels=4;
      for(let i=0;i<d.length;i+=4){
        d[i]=Math.round(d[i]/255*levels)/levels*255;
        d[i+1]=Math.round(d[i+1]/255*levels)/levels*255;
        d[i+2]=Math.round(d[i+2]/255*levels)/levels*255;
      }
    }
    else if(fid===FLT.THRESHOLD){
      const t=128;
      for(let i=0;i<d.length;i+=4){
        const lum=d[i]*0.3+d[i+1]*0.59+d[i+2]*0.11;
        const v=lum>t?255:0;
        d[i]=d[i+1]=d[i+2]=v;
      }
    }
    else if(fid===FLT.CHROMATIC){
      const src=new Uint8ClampedArray(d);
      const intensity=6;const cx2=w/2,cy2=h/2;
      for(let y=0;y<h;y++)for(let x=0;x<w;x++){
        const dx2=x-cx2,dy2=y-cy2;
        const dist=Math.sqrt(dx2*dx2+dy2*dy2)/Math.sqrt(cx2*cx2+cy2*cy2);
        const off=Math.round(dist*intensity);
        const di=(y*w+x)*4;
        // Red channel shifted outward, blue shifted inward
        const rx=Math.max(0,Math.min(w-1,x+Math.round(dx2/Math.max(1,Math.sqrt(dx2*dx2+dy2*dy2))*off)));
        const ry=Math.max(0,Math.min(h-1,y+Math.round(dy2/Math.max(1,Math.sqrt(dx2*dx2+dy2*dy2))*off)));
        const bx=Math.max(0,Math.min(w-1,x-Math.round(dx2/Math.max(1,Math.sqrt(dx2*dx2+dy2*dy2))*off)));
        const by=Math.max(0,Math.min(h-1,y-Math.round(dy2/Math.max(1,Math.sqrt(dx2*dx2+dy2*dy2))*off)));
        d[di]=src[(ry*w+rx)*4];     // Red from shifted position
        d[di+1]=src[(y*w+x)*4+1];   // Green stays
        d[di+2]=src[(by*w+bx)*4+2]; // Blue from opposite shift
      }
    }
    else if(fid===FLT.PIXELATE){
      const blockSize=8;
      for(let by=0;by<h;by+=blockSize)for(let bx=0;bx<w;bx+=blockSize){
        let sr=0,sg=0,sb=0,cnt=0;
        const bw=Math.min(blockSize,w-bx),bh=Math.min(blockSize,h-by);
        for(let py=0;py<bh;py++)for(let px=0;px<bw;px++){
          const si=((by+py)*w+(bx+px))*4;sr+=d[si];sg+=d[si+1];sb+=d[si+2];cnt++;
        }
        const ar=sr/cnt,ag=sg/cnt,ab=sb/cnt;
        for(let py=0;py<bh;py++)for(let px=0;px<bw;px++){
          const di=((by+py)*w+(bx+px))*4;d[di]=ar;d[di+1]=ag;d[di+2]=ab;
        }
      }
    }
  }

  // ── Gaussian Blur — routes through KernelRegistry when available ──
  function applyGaussianBlur(radius){
    const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();return;}pushUndo("Gaussian Blur");
    const r=Math.max(1,Math.min(100,Math.round(radius)));
    const w=state.w,h=state.h;
    const id=L.x.getImageData(0,0,w,h);
    const K=state._bridge;
    if(K&&typeof K.run==='function'){
      K.run("gaussian_blur",id.data,w,h,{radius:r});
    } else if(K&&K._wasmAvailable){
      K.runKernel("gaussian_blur",id.data,w,h,{radius:r});
    } else {
      // JS fallback — synchronous separable two-pass
      const d=id.data,sigma=r/3;
      const kSize=r*2+1;const kernel=new Float32Array(kSize);let kSum=0;
      for(let i=-r;i<=r;i++){const w2=Math.exp(-(i*i)/(2*sigma*sigma));kernel[i+r]=w2;kSum+=w2;}
      for(let i=0;i<kSize;i++)kernel[i]/=kSum;
      const tmp=new Float32Array(w*h*4);
      for(let y=0;y<h;y++)for(let x=0;x<w;x++){let rr=0,gg=0,bb=0,aa=0;for(let k=-r;k<=r;k++){const nx=Math.max(0,Math.min(w-1,x+k));const si=(y*w+nx)*4;const wt=kernel[k+r];rr+=d[si]*wt;gg+=d[si+1]*wt;bb+=d[si+2]*wt;aa+=d[si+3]*wt;}const di=(y*w+x)*4;tmp[di]=rr;tmp[di+1]=gg;tmp[di+2]=bb;tmp[di+3]=aa;}
      for(let y=0;y<h;y++)for(let x=0;x<w;x++){let rr=0,gg=0,bb=0,aa=0;for(let k=-r;k<=r;k++){const ny=Math.max(0,Math.min(h-1,y+k));const si=(ny*w+x)*4;const wt=kernel[k+r];rr+=tmp[si]*wt;gg+=tmp[si+1]*wt;bb+=tmp[si+2]*wt;aa+=tmp[si+3]*wt;}const di=(y*w+x)*4;d[di]=(rr+0.5)|0;d[di+1]=(gg+0.5)|0;d[di+2]=(bb+0.5)|0;d[di+3]=(aa+0.5)|0;}
    }
    L.x.putImageData(id,0,0);
    state.history.push({icon:"🔵",label:`Gaussian Blur (r=${r})`});
  }

  // Selection from layer alpha channel
  function selFromAlpha(layerIdx,mode){
    const L=state.layers[layerIdx];if(!L)return null;
    const id=L.x.getImageData(0,0,state.w,state.h).data;
    const m=new Uint8Array(state.w*state.h);
    let minX=state.w,minY=state.h,maxX=0,maxY=0;
    for(let i=0;i<m.length;i++){
      m[i]=id[i*4+3];
      if(m[i]>0){const x=i%state.w,y=(i/state.w)|0;minX=Math.min(minX,x);minY=Math.min(minY,y);maxX=Math.max(maxX,x);maxY=Math.max(maxY,y);}
    }
    if(maxX<minX)return null;
    return{mask:m,x:minX,y:minY,w:maxX-minX+1,h:maxY-minY+1,t:"alpha"};
  }

  // ── Render layer effects (shadow, glow, overlay, etc.) ──
  // Called from both Canvas2D path and GL post-pass
  // ── Render layer effects with caching ──
  // All effects render into l._fxCache and only recompute when layer/fx change.
  function renderLayerFx(ctx,l){
    if(!l.fx)return;
    const hasFx=l.fx.shadow||l.fx.bevel||l.fx.outline||l.fx.innerShadow||l.fx.outerGlow||l.fx.innerGlow||l.fx.colorOverlay||l.fx.gradOverlay;
    if(!hasFx)return;

    // Cache key: only recompute when fx params change
    const fxKey=JSON.stringify(l.fx);
    const dirty=!l._fxCache||l._fxCacheKey!==fxKey||l._fxCacheW!==state.w||l._fxCacheH!==state.h||l._fxDirty;

    if(dirty){
      if(!l._fxCache)l._fxCache=new OffscreenCanvas(state.w,state.h);
      if(l._fxCache.width!==state.w)l._fxCache.width=state.w;
      if(l._fxCache.height!==state.h)l._fxCache.height=state.h;
      const fx=l._fxCache.getContext("2d",{willReadFrequently:true});
      fx.clearRect(0,0,state.w,state.h);

      // Drop Shadow
      if(l.fx.shadow){
        const sh=l.fx.shadow;
        fx.save();fx.shadowColor=sh.color||"rgba(0,0,0,0.5)";
        fx.shadowBlur=sh.blur||8;fx.shadowOffsetX=sh.ox||4;fx.shadowOffsetY=sh.oy||4;
        fx.drawImage(l.c,0,0);fx.restore();
        fx.drawImage(l.c,0,0); // redraw layer on top to cover shadow underneath
      }

      // Bevel & Emboss
      if(l.fx.bevel){
        const bv=l.fx.bevel,sz2=bv.size||2;
        const{canvas:bc,ctx:bx2}=renderCache.getCanvas('bevel',state.w,state.h);
        bx2.clearRect(0,0,state.w,state.h);
        const bid=l.x.getImageData(0,0,state.w,state.h);
        const bimg=bx2.createImageData(state.w,state.h);const bd=bimg.data;
        const bAlpha=bv.opacity||0.5;
        const w=state.w,h=state.h;
        for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
          const idx=y*w+x;const a=bid.data[idx*4+3];if(a<10)continue;
          const aL=bid.data[(y*w+x-1)*4+3],aT=bid.data[((y-1)*w+x)*4+3];
          const aR=bid.data[(y*w+x+1)*4+3],aB=bid.data[((y+1)*w+x)*4+3];
          const lightStr=Math.max(0,(a-aL)+(a-aT))/(255*2);
          const shadowStr=Math.max(0,(a-aR)+(a-aB))/(255*2);
          const pi=idx*4;
          if(lightStr>0.01){const str=Math.min(1,lightStr*sz2)*bAlpha;bd[pi]=255;bd[pi+1]=255;bd[pi+2]=255;bd[pi+3]=Math.round(str*0.4*255);}
          else if(shadowStr>0.01){const str=Math.min(1,shadowStr*sz2)*bAlpha;bd[pi]=0;bd[pi+1]=0;bd[pi+2]=0;bd[pi+3]=Math.round(str*0.3*255);}
        }
        bx2.putImageData(bimg,0,0);fx.drawImage(bc,0,0);
      }

      // Outline/Stroke
      if(l.fx.outline){
        const ol=l.fx.outline,osz=ol.size||2;
        const{canvas:oc,ctx:ox}=renderCache.getCanvas('outline',state.w,state.h);
        ox.clearRect(0,0,state.w,state.h);
        const oid=l.x.getImageData(0,0,state.w,state.h);
        const oimg=ox.createImageData(state.w,state.h);const od=oimg.data;
        const olc=ol.color||"#fff";let oR=255,oG=255,oB=255;
        if(olc.startsWith("#")&&olc.length>=7){oR=parseInt(olc.slice(1,3),16);oG=parseInt(olc.slice(3,5),16);oB=parseInt(olc.slice(5,7),16);}
        const oAlpha=ol.opacity||1;
        const w=state.w,h=state.h;
        for(let py=0;py<h;py++)for(let px=0;px<w;px++){
          if(oid.data[(py*w+px)*4+3]>=10)continue;
          let minDist=osz+2;const scanR=osz+1;
          for(let dy=-scanR;dy<=scanR&&minDist>0;dy++)for(let dx=-scanR;dx<=scanR&&minDist>0;dx++){
            const nx=px+dx,ny=py+dy;if(nx<0||nx>=w||ny<0||ny>=h)continue;
            if(oid.data[(ny*w+nx)*4+3]>=10){const d2=Math.sqrt(dx*dx+dy*dy);if(d2<minDist)minDist=d2;}
          }
          if(minDist<=osz+0.5){
            const alpha=Math.max(0,Math.min(1,1-(minDist-0.5)/osz));
            const idx=(py*w+px)*4;od[idx]=oR;od[idx+1]=oG;od[idx+2]=oB;od[idx+3]=Math.round(alpha*oAlpha*255);
          }
        }
        ox.putImageData(oimg,0,0);fx.drawImage(oc,0,0);
      }

      // Inner Shadow
      if(l.fx.innerShadow){
        const is=l.fx.innerShadow;
        const{canvas:ic,ctx:ix}=renderCache.getCanvas('innerShadow',state.w,state.h);
        ix.clearRect(0,0,state.w,state.h);ix.fillStyle="#000";ix.fillRect(0,0,state.w,state.h);
        ix.globalCompositeOperation="destination-out";ix.drawImage(l.c,0,0);ix.globalCompositeOperation="source-over";
        const{canvas:sc,ctx:sx}=renderCache.getCanvas('innerShadow2',state.w,state.h);
        sx.clearRect(0,0,state.w,state.h);
        sx.shadowColor=is.color||"rgba(0,0,0,0.6)";sx.shadowBlur=is.blur||6;
        sx.shadowOffsetX=is.ox||2;sx.shadowOffsetY=is.oy||2;sx.drawImage(ic,0,0);
        sx.shadowColor="transparent";sx.shadowBlur=0;sx.shadowOffsetX=0;sx.shadowOffsetY=0;
        sx.globalCompositeOperation="destination-in";sx.drawImage(l.c,0,0);sx.globalCompositeOperation="source-over";
        fx.save();fx.globalAlpha=is.opacity||0.7;fx.drawImage(sc,0,0);fx.restore();
      }

      // Outer Glow
      if(l.fx.outerGlow){
        const og=l.fx.outerGlow;
        const{canvas:gc,ctx:gx}=renderCache.getCanvas('outerGlow',state.w,state.h);
        gx.clearRect(0,0,state.w,state.h);gx.drawImage(l.c,0,0);
        if(og.spread>0){for(let s=0;s<Math.min(og.spread,10);s++){gx.drawImage(gc,-1,0);gx.drawImage(gc,1,0);gx.drawImage(gc,0,-1);gx.drawImage(gc,0,1);}}
        gx.globalCompositeOperation="source-in";gx.fillStyle=og.color||"#fbbf24";gx.fillRect(0,0,state.w,state.h);gx.globalCompositeOperation="source-over";
        const{canvas:bc,ctx:bx}=renderCache.getCanvas('outerGlowBlur',state.w,state.h);
        bx.clearRect(0,0,state.w,state.h);bx.filter="blur("+Math.min(40,og.size||10)+"px)";bx.drawImage(gc,0,0);bx.filter="none";
        bx.globalCompositeOperation="destination-out";bx.drawImage(l.c,0,0);bx.globalCompositeOperation="source-over";
        fx.save();fx.globalAlpha=og.opacity||0.6;fx.globalCompositeOperation="lighter";fx.drawImage(bc,0,0);fx.restore();
      }

      // Inner Glow
      if(l.fx.innerGlow){
        const ig=l.fx.innerGlow;
        const{canvas:gc,ctx:gx}=renderCache.getCanvas('innerGlow',state.w,state.h);
        gx.clearRect(0,0,state.w,state.h);gx.fillStyle="#000";gx.fillRect(0,0,state.w,state.h);
        gx.globalCompositeOperation="destination-out";gx.drawImage(l.c,0,0);gx.globalCompositeOperation="source-over";
        gx.globalCompositeOperation="source-in";gx.fillStyle=ig.color||"#ffffff";gx.fillRect(0,0,state.w,state.h);gx.globalCompositeOperation="source-over";
        const{canvas:bc,ctx:bx}=renderCache.getCanvas('innerGlowBlur',state.w,state.h);
        bx.clearRect(0,0,state.w,state.h);bx.filter="blur("+Math.min(30,ig.size||8)+"px)";bx.drawImage(gc,0,0);bx.filter="none";
        bx.globalCompositeOperation="destination-in";bx.drawImage(l.c,0,0);bx.globalCompositeOperation="source-over";
        fx.save();fx.globalAlpha=ig.opacity||0.5;fx.drawImage(bc,0,0);fx.restore();
      }

      // Color Overlay
      if(l.fx.colorOverlay){
        const co=l.fx.colorOverlay;
        const{canvas:cc,ctx:cx2}=renderCache.getCanvas('colorOverlay',state.w,state.h);
        cx2.clearRect(0,0,state.w,state.h);cx2.fillStyle=co.color||"#f87171";cx2.fillRect(0,0,state.w,state.h);
        cx2.globalCompositeOperation="destination-in";cx2.drawImage(l.c,0,0);cx2.globalCompositeOperation="source-over";
        fx.save();fx.globalAlpha=co.opacity||0.5;fx.globalCompositeOperation=co.blend||"source-over";fx.drawImage(cc,0,0);fx.restore();
      }

      // Gradient Overlay
      if(l.fx.gradOverlay){
        const go=l.fx.gradOverlay;
        const{canvas:gc,ctx:gx}=renderCache.getCanvas('gradOverlay',state.w,state.h);
        gx.clearRect(0,0,state.w,state.h);
        const ang=(go.angle||0)*Math.PI/180;const cx2=state.w/2,cy2=state.h/2;const len=Math.max(state.w,state.h);
        const grad=gx.createLinearGradient(cx2-Math.cos(ang)*len/2,cy2-Math.sin(ang)*len/2,cx2+Math.cos(ang)*len/2,cy2+Math.sin(ang)*len/2);
        grad.addColorStop(0,go.color1||"#000000");grad.addColorStop(1,go.color2||"#ffffff");
        gx.fillStyle=grad;gx.fillRect(0,0,state.w,state.h);
        gx.globalCompositeOperation="destination-in";gx.drawImage(l.c,0,0);gx.globalCompositeOperation="source-over";
        fx.save();fx.globalAlpha=go.opacity||0.5;fx.drawImage(gc,0,0);fx.restore();
      }

      l._fxCacheKey=fxKey;l._fxCacheW=state.w;l._fxCacheH=state.h;l._fxDirty=false;
    }

    // Draw cached FX
    ctx.save();ctx.globalAlpha=l.op;ctx.drawImage(l._fxCache,0,0);ctx.restore();
  }


  function handleCommand(c) {
    const t = c.type, pl = c.payload;

    // Mark all layers as GPU-dirty — commands are infrequent user actions
    for (const l of state.layers) { l._glDirty = true; l._fxDirty = true; }

    // Undo/redo handled separately — takeSnapshot needs _undoClean intact
    if (t === C.UNDO) {
      if (state.undo.length) {
        state.redo.push(takeSnapshot());
        restoreSnapshot(state.undo.pop());
        state.history.push({ icon: "↩", label: "Undo" });
      }
      return;
    }
    if (t === C.REDO) {
      if (state.redo.length) {
        state.undo.push(takeSnapshot());
        restoreSnapshot(state.redo.pop());
        state.history.push({ icon: "↪", label: "Redo" });
      }
      return;
    }

switch(t){
      // ════════════════ LAYER MANAGEMENT ════════════════
      case C.LAYER_ADD:
        pushUndo("Add Layer");
        state.layers.push(mkL("Layer " + state.layers.length, state.w, state.h));
        state.act = state.layers.length - 1;
        break;
      case C.LAYER_DEL:if(state.layers.length>1){
        const delIdx=pl.idx??state.act;
        const delL=state.layers[delIdx];
        if(delL&&delL.type==="group"&&delL.children&&delL.children.length>0){
          const mode=pl.mode||"release"; // "release" or "delete"
          pushUndo("Delete Group");
          if(mode==="delete"){
            // Delete children too (sort descending so splice indices stay valid)
            const toRemove=[delIdx,...delL.children].sort((a,b)=>b-a);
            for(const ri of toRemove)state.layers.splice(ri,1);
          } else {
            // Release children: just remove the group, children stay
            state.layers.splice(delIdx,1);
          }
          // Fix child indices in all remaining groups
          for(const gl of state.layers){if(gl.type==="group"&&gl.children){
            gl.children=gl.children.map(ci=>ci>delIdx?ci-1:ci).filter(ci=>ci>=0&&ci<state.layers.length);
          }}
        } else {
          pushUndo("Delete Layer");
          // Remove this layer from any group's children
          for(const gl of state.layers){if(gl.type==="group"&&gl.children)gl.children=gl.children.filter(ci=>ci!==delIdx);}
          state.layers.splice(delIdx,1);
          // Fix child indices in all remaining groups
          for(const gl of state.layers){if(gl.type==="group"&&gl.children){
            gl.children=gl.children.map(ci=>ci>delIdx?ci-1:ci).filter(ci=>ci>=0&&ci<state.layers.length);
          }}
        }
        state.act=Math.min(state.act,state.layers.length-1);
      }break;
      case C.LAYER_CLR:{const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}pushUndo("Clear");if(state.sel)clearThroughMask(L);else L.x.clearRect(0,0,state.w,state.h);}break;
      case C.LAYER_UP:{const li=pl.idx??state.act;if(li<state.layers.length-1){pushUndo("Reorder");[state.layers[li],state.layers[li+1]]=[state.layers[li+1],state.layers[li]];state.act=li+1;}}break;
      case C.LAYER_DN:{const li=pl.idx??state.act;if(li>0){pushUndo("Reorder");[state.layers[li],state.layers[li-1]]=[state.layers[li-1],state.layers[li]];state.act=li-1;}}break;
      case C.LAYER_REORDER:{const from=pl.from,to=pl.to;if(from!=null&&to!=null&&from!==to&&from>=0&&from<state.layers.length&&to>=0&&to<state.layers.length){pushUndo("Reorder");const layer=state.layers.splice(from,1)[0];state.layers.splice(to,0,layer);state.act=to;
        // Fix group children indices after reorder
        for(const gl of state.layers){if(gl.type==="group"&&gl.children){
          gl.children=gl.children.map(ci=>{
            if(ci===from)return to;
            if(from<to){if(ci>from&&ci<=to)return ci-1;}
            else{if(ci>=to&&ci<from)return ci+1;}
            return ci;
          });
        }}
      }}break;
      // ════════════════ STROKE & DRAWING ════════════════
      case C.STROKE_ON:
        if (!state.strokeOn) {
          pushUndo("Brush stroke");
          state.strokeOn = true;
          state.lastPt = null;
          state._ptRing = [];
        }
        break;
      case C.STROKE_OFF:
        state.strokeOn = false;
        state.lastPt = null;
        state.stampCompDirty = false;
        state._ptRing = [];
        break;
      // ════════════════ SELECTION ════════════════
      case C.SEL_RECT: {
        pushUndo("Selection");
        const ns = selFromRect(pl.x, pl.y, pl.w, pl.h);
        if (pl.sub) selSubtract(ns);
        else if (pl.add) selMerge(ns);
        else state.sel = ns;
      } break;
      case C.SEL_ELLIPSE:{pushUndo("Selection");const ns=selFromEllipse(pl.x,pl.y,pl.w,pl.h);if(pl.sub)selSubtract(ns);else if(pl.add)selMerge(ns);else setSel(ns);}break;
      case C.SEL_CLR:
        if (state.sel) pushUndo("Deselect");
        state.sel = null;
        break;
      case C.SEL_ALL:
        pushUndo("Select All");
        state.sel = selFromRect(0, 0, state.w, state.h);
        break;
      case C.SEL_INV:pushUndo("Invert Selection");selInvert();break;
      case C.SEL_LASSO:{pushUndo("Lasso");const ns=selFromLasso(pl.points);if(ns){if(pl.sub)selSubtract(ns);else if(pl.add)selMerge(ns);else setSel(ns);}}break;
      case C.SEL_POLY:{pushUndo("Poly Lasso");const ns=selFromLasso(pl.points);if(ns){if(pl.sub)selSubtract(ns);else if(pl.add)selMerge(ns);else setSel(ns);}}break;
      case C.SEL_WAND:{pushUndo("Magic Wand");const ws=selFromWand(pl.x,pl.y,pl.tolerance||30);if(ws){if(pl.sub)selSubtract(ws);else if(pl.add)selMerge(ws);else setSel(ws);}}break;
      case C.SEL_FEATHER:pushUndo("Feather");selFeather(pl.radius||5);break;
      // ════════════════ TRANSFORM ════════════════
      case C.TFM_BEGIN:{const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}
        const id=L.x.getImageData(0,0,state.w,state.h);const d=id.data;
        let bx0=state.w,by0=state.h,bx1=0,by1=0;
        if(state.sel&&state.sel.mask){
          // Selection active: use selection bounds and extract only selected pixels
          const m=state.sel.mask;
          for(let py=0;py<state.h;py++)for(let px=0;px<state.w;px++){
            const idx=py*state.w+px;
            if(m[idx]>=128&&d[idx*4+3]>0){bx0=Math.min(bx0,px);by0=Math.min(by0,py);bx1=Math.max(bx1,px);by1=Math.max(by1,py);}
          }
          if(bx1<bx0){bx0=state.sel.x;by0=state.sel.y;bx1=state.sel.x+state.sel.w-1;by1=state.sel.y+state.sel.h-1;}
          // Snapshot the full original layer for cancel/restore
          const backupData=L.x.getImageData(0,0,state.w,state.h);
          // Create origData with only the selected pixels (clear unselected)
          const selData=L.x.getImageData(0,0,state.w,state.h);const sd2=selData.data;
          for(let i=0;i<m.length;i++){if(m[i]<128){sd2[i*4]=0;sd2[i*4+1]=0;sd2[i*4+2]=0;sd2[i*4+3]=0;}}
          // Clear selected pixels from the layer (they'll be drawn back on apply)
          for(let i=0;i<m.length;i++){if(m[i]>=128){d[i*4+3]=Math.round(d[i*4+3]*(1-m[i]/255));}}
          L.x.putImageData(id,0,0);
          const bw=bx1-bx0+1,bh=by1-by0+1;
          state.tfm={x:bx0,y:by0,w:bw,h:bh,rot:0,origData:selData,origX:bx0,origY:by0,origW:bw,origH:bh,fromSel:true,layerBackup:backupData};
        } else {
          // No selection: scan for tight bounding box of non-transparent pixels
          for(let py=0;py<state.h;py++)for(let px=0;px<state.w;px++){if(d[(py*state.w+px)*4+3]>0){bx0=Math.min(bx0,px);by0=Math.min(by0,py);bx1=Math.max(bx1,px);by1=Math.max(by1,py);}}
          if(bx1<bx0){bx0=0;by0=0;bx1=state.w-1;by1=state.h-1;}
          const bw=bx1-bx0+1,bh=by1-by0+1;
          state.tfm={x:bx0,y:by0,w:bw,h:bh,rot:0,origData:id,origX:bx0,origY:by0,origW:bw,origH:bh};
        }
        state.history.push({icon:"⬡",label:"Transform"});}break;
      case C.TFM_APPLY:if(state.tfm){pushUndo("Apply Transform");const L=state.layers[state.act];if(L){
        const tc=new OffscreenCanvas(state.w,state.h);const tcx=tc.getContext("2d",{willReadFrequently:true});tcx.imageSmoothingEnabled=true;tcx.imageSmoothingQuality="high";tcx.putImageData(state.tfm.origData,0,0);
        const skX=Math.tan((state.tfm.skewX||0)*Math.PI/180);
        const skY=Math.tan((state.tfm.skewY||0)*Math.PI/180);
        if(state.tfm.fromSel){
          const cx3=state.tfm.x+state.tfm.w/2,cy3=state.tfm.y+state.tfm.h/2;
          L.x.imageSmoothingEnabled=true;L.x.imageSmoothingQuality="high";
          L.x.save();L.x.translate(cx3,cy3);L.x.rotate((state.tfm.rot||0)*Math.PI/180);
          if(skX||skY)L.x.transform(1,skY,skX,1,0,0);
          L.x.drawImage(tc,state.tfm.origX,state.tfm.origY,state.tfm.origW,state.tfm.origH,-state.tfm.w/2,-state.tfm.h/2,state.tfm.w,state.tfm.h);
          L.x.restore();
        } else {
          L.x.clearRect(0,0,state.w,state.h);
          const cx3=state.tfm.x+state.tfm.w/2,cy3=state.tfm.y+state.tfm.h/2;
          L.x.imageSmoothingEnabled=true;L.x.imageSmoothingQuality="high";
          L.x.save();L.x.translate(cx3,cy3);L.x.rotate((state.tfm.rot||0)*Math.PI/180);
          if(skX||skY)L.x.transform(1,skY,skX,1,0,0);
          L.x.drawImage(tc,state.tfm.origX,state.tfm.origY,state.tfm.origW,state.tfm.origH,-state.tfm.w/2,-state.tfm.h/2,state.tfm.w,state.tfm.h);
          L.x.restore();
        }
      }}state.tfm=null;clearSel();break;
      case C.TFM_UPDATE:if(state.tfm){
        if(pl.x!=null)state.tfm.x=pl.x;
        if(pl.y!=null)state.tfm.y=pl.y;
        if(pl.w!=null)state.tfm.w=pl.w;
        if(pl.h!=null)state.tfm.h=pl.h;
        if(pl.rot!=null)state.tfm.rot=pl.rot;
        if(pl.skewX!=null)state.tfm.skewX=pl.skewX;
        if(pl.skewY!=null)state.tfm.skewY=pl.skewY;
      }break;
      case C.TFM_CANCEL:if(state.tfm){const L=state.layers[state.act];if(L){
        if(state.tfm.fromSel&&state.tfm.layerBackup){
          // Restore the full original layer from before the selection was extracted
          L.x.putImageData(state.tfm.layerBackup,0,0);
        } else if(state.tfm.selOnly){
          // Selection-only transform cancel: restore original selection mask
          setSel(state.tfm.origSel);
        } else if(state.tfm.origData){
          L.x.putImageData(state.tfm.origData,0,0);
        }
      }}state.tfm=null;break;
      case C.TFM_SEL_BEGIN:{
        // Transform the selection mask itself, not the pixels
        if(!state.sel||!state.sel.mask)break;
        pushUndo("Transform Selection");
        const m=state.sel,bx0=m.x,by0=m.y,bw=m.w,bh=m.h;
        // Render the selection mask as an image for preview
        const mc=new OffscreenCanvas(state.w,state.h);const mx=mc.getContext("2d",{willReadFrequently:true});
        const mid=mx.createImageData(state.w,state.h);
        for(let i=0;i<m.mask.length;i++){const a=m.mask[i];mid.data[i*4]=255;mid.data[i*4+1]=255;mid.data[i*4+2]=255;mid.data[i*4+3]=a;}
        mx.putImageData(mid,0,0);
        state.tfm={x:bx0,y:by0,w:bw,h:bh,rot:0,origData:mid,origX:bx0,origY:by0,origW:bw,origH:bh,selOnly:true,origSel:{...m,mask:new Uint8Array(m.mask)}};
        state.history.push({icon:"⬡",label:"Transform Selection"});
      }break;
      case C.TFM_SEL_APPLY:{
        if(!state.tfm||!state.tfm.selOnly)break;
        pushUndo("Apply Sel Transform");
        // Rebuild selection mask from the transformed bounds
        const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d",{willReadFrequently:true});
        tx.imageSmoothingEnabled=true;tx.imageSmoothingQuality="high";
        // Draw original mask data
        const origC=new OffscreenCanvas(state.w,state.h);origC.getContext("2d",{willReadFrequently:true}).putImageData(state.tfm.origData,0,0);
        // Apply transform
        const cx3=state.tfm.x+state.tfm.w/2,cy3=state.tfm.y+state.tfm.h/2;
        tx.save();tx.translate(cx3,cy3);tx.rotate((state.tfm.rot||0)*Math.PI/180);
        tx.drawImage(origC,state.tfm.origX,state.tfm.origY,state.tfm.origW,state.tfm.origH,-state.tfm.w/2,-state.tfm.h/2,state.tfm.w,state.tfm.h);
        tx.restore();
        // Read back as new selection mask
        const rid=tx.getImageData(0,0,state.w,state.h);
        const nm=new Uint8Array(state.w*state.h);
        let minX=state.w,minY=state.h,maxX=0,maxY=0;
        for(let i=0;i<nm.length;i++){
          nm[i]=rid.data[i*4+3];
          if(nm[i]>0){const px=i%state.w,py=(i/state.w)|0;minX=Math.min(minX,px);minY=Math.min(minY,py);maxX=Math.max(maxX,px);maxY=Math.max(maxY,py);}
        }
        if(maxX>=minX){setSel({mask:nm,x:minX,y:minY,w:maxX-minX+1,h:maxY-minY+1,t:"tfm"});}
        else{clearSel();}
        state.tfm=null;state.dirty=true;
        state.history.push({icon:"⬡",label:"Apply Sel Transform"});
      }break;
      // ════════════════ CLIPBOARD & MOVE ════════════════
      case C.COPY:if(state.sel){const L=state.layers[state.act];state.clip=copyThroughMask(L);state.history.push({icon:"📋",label:"Copy"});}break;
      case C.CUT:if(state.sel){const L=state.layers[state.act];state.clip=copyThroughMask(L);pushUndo("Cut");clearThroughMask(L);}break;
      case C.PASTE:if(state.clip){pushUndo("Paste");const nl=mkL("Pasted",state.w,state.h);if(state.clip.full)nl.x.putImageData(state.clip.d,0,0);else nl.x.putImageData(state.clip.d,pl.x||0,pl.y||0);state.layers.push(nl);state.act=state.layers.length-1;}break;
      case C.CROP:if(pl.w>1&&pl.h>1){pushUndo("Crop");const cx=Math.round(pl.x),cy=Math.round(pl.y),cw2=Math.round(pl.w),ch2=Math.round(pl.h);for(const l of state.layers){const id=l.x.getImageData(cx,cy,cw2,ch2);l.c.width=cw2;l.c.height=ch2;l.x=l.c.getContext("2d",{willReadFrequently:true});l.x.putImageData(id,0,0);const mid=l.maskCtx.getImageData(cx,cy,cw2,ch2);l.mask.width=cw2;l.mask.height=ch2;l.maskCtx=l.mask.getContext("2d",{willReadFrequently:true});l.maskCtx.putImageData(mid,0,0);}state.w=cw2;state.h=ch2;clearSel();state.tfm=null;if(state.onDimsChanged)state.onDimsChanged(cw2,ch2);}break;
      case C.MOVE:{const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}pushUndo("Move Layer");
        if(L.textData){
          // Text layer: update text position metadata and re-render (no pixel-level shift)
          L.textData.x=(L.textData.x||0)+(pl.dx||0);
          L.textData.y=(L.textData.y||0)+(pl.dy||0);
          renderTextLayer(L);
        } else {
          const id=L.x.getImageData(0,0,state.w,state.h);L.x.clearRect(0,0,state.w,state.h);L.x.putImageData(id,pl.dx||0,pl.dy||0);
        }
        if(L.maskLinked){const mid=L.maskCtx.getImageData(0,0,state.w,state.h);L.maskCtx.clearRect(0,0,state.w,state.h);L.maskCtx.putImageData(mid,pl.dx||0,pl.dy||0);}}break;
      // ════════════════ FILTERS & ADJUSTMENTS ════════════════
      case C.VIS:pushUndo("Visibility");{const i=pl.idx??state.act;if(state.layers[i])state.layers[i].vis=!state.layers[i].vis;}break;
      case C.FILTER:filter(pl.id??0);state.history.push({icon:"✨",label:["Auto","Blur","Sharpen","Gray","Invert","Sepia","Vignette","Noise","HueSat","Emboss","Edge Detect","Posterize","Threshold","Chromatic","Pixelate"][pl.id]||"Filter"});break;
      case C.TEXT_ADD:{pushUndo("Add Text");const nl=mkL("Text: "+pl.text.slice(0,12),state.w,state.h);nl.textData={text:pl.text,font:pl.font||"sans-serif",size:pl.size||32,weight:pl.weight||"normal",color:pl.color||"#000",align:pl.align||"left",x:pl.x||20,y:pl.y||20};renderTextLayer(nl);state.layers.push(nl);state.act=state.layers.length-1;}break;
      case C.TEXT_UPDATE:{const L=state.layers[pl.idx??state.act];if(L&&L.textData){pushUndo("Edit Text");L.textData={...L.textData,...pl};L.name="Text: "+(L.textData.text||"").slice(0,12);renderTextLayer(L);}}break;
      case C.FX_UPDATE:pushUndo("Layer FX");{const i=pl.layerIdx??state.act;if(state.layers[i])state.layers[i].fx={...state.layers[i].fx,...pl.fx};}break;
      case C.LAYER_OP:pushUndo("Opacity");{const i=pl.idx??state.act;if(state.layers[i])state.layers[i].op=pl.op;}break;
      case C.LAYER_BLEND:pushUndo("Blend Mode");{const i=pl.idx??state.act;if(state.layers[i])state.layers[i].blend=BLEND_MAP[pl.blend]||"source-over";}break;
      case C.MERGE_DOWN:if(state.act>0&&state.layers.length>1){const top=state.layers[state.act],bot=state.layers[state.act-1];if(!top.vis&&!bot.vis)break;pushUndo("Merge Down");bot.x.globalAlpha=top.op;bot.x.globalCompositeOperation=top.blend;bot.x.drawImage(top.c,0,0);bot.x.globalAlpha=1;bot.x.globalCompositeOperation="source-over";state.layers.splice(state.act,1);state.act--;}break;
      case C.FLATTEN:if(state.layers.length>1){pushUndo("Flatten");const nc=new OffscreenCanvas(state.w,state.h);const nx=nc.getContext("2d",{willReadFrequently:true});for(const l of state.layers){if(!l.vis)continue;nx.globalAlpha=l.op;nx.globalCompositeOperation=l.blend;nx.drawImage(l.c,0,0);}nx.globalAlpha=1;nx.globalCompositeOperation="source-over";state.layers=[mkL("Flattened",state.w,state.h)];state.layers[0].x.drawImage(nc,0,0);state.act=0;}break;
      case C.MASK_ADD:pushUndo("Add Mask");{const L=state.layers[state.act];if(L){L.maskEnabled=true;L.maskCtx.fillStyle="#fff";L.maskCtx.fillRect(0,0,state.w,state.h);}}break;
      case C.MASK_DEL:pushUndo("Del Mask");{const L=state.layers[state.act];if(L)L.maskEnabled=false;}break;
      case C.MASK_TOGGLE:pushUndo("Toggle Mask");{const ii=pl.idx??state.act;if(state.layers[ii])state.layers[ii].maskEnabled=!state.layers[ii].maskEnabled;}break;
      case C.FILL:{const L=state.layers[state.act];if(L){const{r:fr,g:fg,b:fb}=pl;floodFill(pl.x,pl.y,pl.mode||"solid",fr||0,fg||0,fb||0,pl.r2||255,pl.g2||255,pl.b2||255,pl.opacity||1,pl.tolerance||30,pl.x2,pl.y2,pl.stops);}}break;
      case C.RUBBER_STAMP_SET:state.stampSrc={srcX:pl.x,srcY:pl.y,offX:0,offY:0,firstClick:true};state.stampCompDirty=false;state.history.push({icon:"⊜",label:"Stamp source set"});break;
      case C.FILL_COLOR:{const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}pushUndo("Fill Color");
        const r=pl.r||0,g=pl.g||0,b=pl.b||0;
        if(state.sel&&state.sel.mask){
          if(pl.strokeOnly){
            // Stroke selection: draw only the border pixels (edge detection)
            const id=L.x.getImageData(0,0,state.w,state.h);const d=id.data;const m=state.sel.mask;const w=state.w,h=state.h;
            const strokeW=pl.strokeWidth||2;
            for(let py=0;py<h;py++)for(let px=0;px<w;px++){
              const idx=py*w+px;
              if(m[idx]<128)continue;
              // Check if this pixel is near the edge (within strokeW of an unselected pixel)
              let isEdge=false;
              for(let dy=-strokeW;dy<=strokeW&&!isEdge;dy++)for(let dx=-strokeW;dx<=strokeW&&!isEdge;dx++){
                const nx=px+dx,ny=py+dy;
                if(nx<0||nx>=w||ny<0||ny>=h){isEdge=true;continue;}
                if(m[ny*w+nx]<128)isEdge=true;
              }
              if(isEdge){const pi=idx*4;d[pi]=r;d[pi+1]=g;d[pi+2]=b;d[pi+3]=255;}
            }
            L.x.putImageData(id,0,0);
            state.history.push({icon:"◻",label:"Stroke Selection"});
          } else {
            // Fill through selection mask
            const id=L.x.getImageData(0,0,state.w,state.h);const d=id.data;const m=state.sel.mask;
            for(let i=0;i<m.length;i++){
              const a=m[i]/255;
              if(a>0){d[i*4]=Math.round(d[i*4]*(1-a)+r*a);d[i*4+1]=Math.round(d[i*4+1]*(1-a)+g*a);d[i*4+2]=Math.round(d[i*4+2]*(1-a)+b*a);d[i*4+3]=Math.max(d[i*4+3],Math.round(a*255));}
            }
            L.x.putImageData(id,0,0);
            state.history.push({icon:"🪣",label:"Fill "+pl.label});
          }
        } else {
          L.x.fillStyle=`rgb(${r},${g},${b})`;L.x.fillRect(0,0,state.w,state.h);
          state.history.push({icon:"🪣",label:"Fill "+pl.label});
        }
      }break;
      // ════════════════ IMAGE OPERATIONS ════════════════
      case C.IMG_FLIP_H:pushUndo("Flip H");{for(const l of state.layers){const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d",{willReadFrequently:true});tx.translate(state.w,0);tx.scale(-1,1);tx.drawImage(l.c,0,0);l.x.clearRect(0,0,state.w,state.h);l.x.drawImage(tc,0,0);}}break;
      case C.IMG_FLIP_V:pushUndo("Flip V");{for(const l of state.layers){const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d",{willReadFrequently:true});tx.translate(0,state.h);tx.scale(1,-1);tx.drawImage(l.c,0,0);l.x.clearRect(0,0,state.w,state.h);l.x.drawImage(tc,0,0);}}break;
      case C.IMG_ROT90:pushUndo("Rotate 90°");{const nw=state.h,nh=state.w;for(const l of state.layers){const tc=new OffscreenCanvas(nw,nh);const tx=tc.getContext("2d",{willReadFrequently:true});tx.translate(nw,0);tx.rotate(Math.PI/2);tx.drawImage(l.c,0,0);l.c.width=nw;l.c.height=nh;l.x=l.c.getContext("2d",{willReadFrequently:true});l.x.drawImage(tc,0,0);l.mask.width=nw;l.mask.height=nh;l.maskCtx=l.mask.getContext("2d",{willReadFrequently:true});l.maskCtx.fillStyle="#fff";l.maskCtx.fillRect(0,0,nw,nh);}state.w=nw;state.h=nh;if(state.onDimsChanged)state.onDimsChanged(nw,nh);}break;
      case C.IMG_ROT180:pushUndo("Rotate 180°");{for(const l of state.layers){const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d",{willReadFrequently:true});tx.translate(state.w,state.h);tx.rotate(Math.PI);tx.drawImage(l.c,0,0);l.x.clearRect(0,0,state.w,state.h);l.x.drawImage(tc,0,0);}}break;
      case C.IMG_RESIZE:{const nw=pl.w,nh=pl.h;if(nw<1||nh<1)break;pushUndo("Image Size");
        for(const l of state.layers){
          const tc=new OffscreenCanvas(nw,nh);const tx=tc.getContext("2d",{willReadFrequently:true});
          tx.imageSmoothingEnabled=true;tx.imageSmoothingQuality="high";
          tx.drawImage(l.c,0,0,nw,nh);
          l.c.width=nw;l.c.height=nh;l.x=l.c.getContext("2d",{willReadFrequently:true});l.x.drawImage(tc,0,0);
          // Resize mask
          const mc=new OffscreenCanvas(nw,nh);const mx=mc.getContext("2d",{willReadFrequently:true});
          mx.imageSmoothingEnabled=true;mx.imageSmoothingQuality="high";
          mx.drawImage(l.mask,0,0,nw,nh);
          l.mask.width=nw;l.mask.height=nh;l.maskCtx=l.mask.getContext("2d",{willReadFrequently:true});l.maskCtx.drawImage(mc,0,0);
          // Re-render text/vector layers at new size
          if(l.textData)renderTextLayer(l);
          if(l.type==="vector"&&l.vectorData)renderVectorLayer(l);
        }
        state.w=nw;state.h=nh;clearSel();state.tfm=null;
        if(state.onDimsChanged)state.onDimsChanged(nw,nh);
        state.history.push({icon:"↔",label:`Resize ${nw}×${nh}`});}break;
      case C.CANVAS_SIZE:{const nw=pl.w,nh=pl.h;if(nw<1||nh<1)break;pushUndo("Canvas Size");
        // Anchor-based positioning (v0.8): 'tl','tc','tr','ml','mc','mr','bl','bc','br'
        const anchor=pl.anchor||"mc";
        let ox=0,oy=0;
        if(anchor[1]==="l")ox=0;else if(anchor[1]==="c"||anchor[1]==="r"&&anchor.length===2&&anchor==="mr")ox=Math.round((nw-state.w)/2);
        // Calculate offsets based on anchor
        const col=anchor.length>=2?anchor[1]:"c";const row=anchor[0];
        if(col==="l")ox=0;else if(col==="c")ox=Math.round((nw-state.w)/2);else if(col==="r")ox=nw-state.w;
        if(row==="t")oy=0;else if(row==="m")oy=Math.round((nh-state.h)/2);else if(row==="b")oy=nh-state.h;
        for(const l of state.layers){
          const tc=new OffscreenCanvas(nw,nh);const tx=tc.getContext("2d",{willReadFrequently:true});
          tx.drawImage(l.c,ox,oy);
          l.c.width=nw;l.c.height=nh;l.x=l.c.getContext("2d",{willReadFrequently:true});l.x.drawImage(tc,0,0);
          const mc=new OffscreenCanvas(nw,nh);const mx=mc.getContext("2d",{willReadFrequently:true});
          mx.fillStyle="#fff";mx.fillRect(0,0,nw,nh);// New mask area is white (visible)
          mx.drawImage(l.mask,ox,oy);
          l.mask.width=nw;l.mask.height=nh;l.maskCtx=l.mask.getContext("2d",{willReadFrequently:true});l.maskCtx.drawImage(mc,0,0);
        }
        state.w=nw;state.h=nh;clearSel();state.tfm=null;
        if(state.onDimsChanged)state.onDimsChanged(nw,nh);
        state.history.push({icon:"⬜",label:`Canvas ${nw}×${nh}`});}break;
      case C.LEVELS:{pushUndo("Levels");const L=state.layers[state.act];if(L){
        const id=L.x.getImageData(0,0,state.w,state.h),d=id.data;
        const B=state._bridge;
        if(B&&B._wasmAvailable){
          B.runKernel("levels",d,state.w,state.h,{inBlack:pl.inBlack||0,inWhite:pl.inWhite||255,gamma:pl.gamma||1,outBlack:pl.outBlack||0,outWhite:pl.outWhite||255});
        } else {
          const iB=pl.inBlack||0,iW=pl.inWhite||255,oB=pl.outBlack||0,oW=pl.outWhite||255,gm=pl.gamma||1;
          const range=iW-iB||1;
          for(let i=0;i<d.length;i+=4){for(let ch=0;ch<3;ch++){let v=(d[i+ch]-iB)/range;v=Math.max(0,Math.min(1,v));v=Math.pow(v,1/gm);d[i+ch]=Math.round(oB+v*(oW-oB));}}
        }
        L.x.putImageData(id,0,0);
        state.history.push({icon:"📊",label:"Levels"});
      }}break;
      case C.MASK_LINK:pushUndo("Link Mask");{const ii=pl.idx??state.act;if(state.layers[ii])state.layers[ii].maskLinked=!state.layers[ii].maskLinked;}break;
      case C.ADJ_LAYER_ADD:{pushUndo("Add Adjustment Layer");const at=pl.adjType;const name="⊕ "+(ADJ_LABELS[at]||at);const params=pl.params||{...(ADJ_DEFAULTS[at]||{})};const nl=mkAdjL(name,at,params,state.w,state.h);nl._adjDirty=true;state.layers.push(nl);state.act=state.layers.length-1;state.history.push({icon:"◑",label:"Add "+name});}break;
      case C.ADJ_LAYER_UPDATE:{const li=pl.idx??state.act;const L=state.layers[li];if(L&&L.type==="adjustment"){
        pushUndo("Edit "+L.name);
        // Apply committed params. adjParams was never mutated by live preview
        // (_adjPreviewParams handled that), so pushUndo() snapshots the real original state.
        L.adjParams={...L.adjParams,...pl.params};
        // Clear any lingering preview — commit supersedes it.
        L._adjPreviewParams=null;
        L._adjDirty=true;L._adjCacheParams=null;
        state.dirty=true;
      }}break;
      case C.HIST_JUMP:{const target=pl.idx;if(target!=null){
        const curLen=state.undo.length;
        if(target<curLen){
          const curSnap=takeSnapshot();
          const stepsBack=curLen-target;
          for(let i=0;i<stepsBack;i++){
            state.redo.push(i===0?curSnap:takeSnapshot());
            if(state.undo.length)restoreSnapshot(state.undo.pop());
          }
          state.history.push({icon:"⏪",label:"History Jump"});
        }
      }}break;
      case C.HUESAT_FILTER:{pushUndo("Hue/Saturation");const L=state.layers[state.act];if(L){
        const id=L.x.getImageData(0,0,state.w,state.h),d=id.data;
        const range=pl.range||"master";
        const B=state._bridge;
        if(B&&B._wasmAvailable&&range==="master"){
          // Wasm path — master range only (no per-hue targeting in wasm kernel yet)
          B.runKernel("hue_saturation",d,state.w,state.h,{hue:pl.hue||0,saturation:pl.saturation||0,lightness:pl.lightness||0});
        } else {
        // JS path — supports targeted hue ranges
        const hShift=(pl.hue||0)/360,sScale=1+(pl.saturation||0)/100,lShift=(pl.lightness||0)/100*0.5;
        const HUE_RANGES={master:null,reds:0,yellows:1/6,greens:2/6,cyans:3/6,blues:4/6,magentas:5/6};
        const center=HUE_RANGES[range];
        const rangeWidth=1/6;const falloff=rangeWidth*0.5;
        for(let i=0;i<d.length;i+=4){
          if(d[i+3]<1)continue;
          let r=d[i]/255,g=d[i+1]/255,b=d[i+2]/255;
          const mx=Math.max(r,g,b),mn=Math.min(r,g,b),dl=mx-mn;
          let h2=0,ss2=0,l2=(mx+mn)/2;
          if(dl>0){ss2=l2>0.5?dl/(2-mx-mn):dl/(mx+mn);if(mx===r)h2=((g-b)/dl+(g<b?6:0))/6;else if(mx===g)h2=((b-r)/dl+2)/6;else h2=((r-g)/dl+4)/6;}
          let strength=1;
          if(center!=null&&dl>0.01){let dist=Math.abs(h2-center);if(dist>0.5)dist=1-dist;if(dist>rangeWidth+falloff)strength=0;else if(dist>rangeWidth)strength=1-(dist-rangeWidth)/falloff;}else if(center!=null){strength=0;}
          if(strength<0.001)continue;
          const effH=hShift*strength,effS=1+(sScale-1)*strength,effL=lShift*strength;
          h2=(h2+effH)%1;if(h2<0)h2+=1;ss2=Math.max(0,Math.min(1,ss2*effS));l2=Math.max(0,Math.min(1,l2+effL));
          if(ss2===0){r=g=b=l2;}else{const hue2rgb=(p,q,t)=>{if(t<0)t+=1;if(t>1)t-=1;if(t<1/6)return p+(q-p)*6*t;if(t<1/2)return q;if(t<2/3)return p+(q-p)*(2/3-t)*6;return p;};const q2=l2<0.5?l2*(1+ss2):l2+ss2-l2*ss2,p2=2*l2-q2;r=hue2rgb(p2,q2,h2+1/3);g=hue2rgb(p2,q2,h2);b=hue2rgb(p2,q2,h2-1/3);}
          d[i]=Math.round(r*255);d[i+1]=Math.round(g*255);d[i+2]=Math.round(b*255);
        }
        }
        L.x.putImageData(id,0,0);
        state.history.push({icon:"🎨",label:"Hue/Sat"+(range!=="master"?" ("+range+")":"")});
      }}break;
      case C.GAUSS_BLUR:applyGaussianBlur(pl.radius||5);break;
      case C.COLOR_BALANCE:{pushUndo("Color Balance");const L=state.layers[state.act];if(L){
        const id=L.x.getImageData(0,0,state.w,state.h);
        applyAdjustment(id,ADJ.COLORBAL,pl,state._bridge);
        L.x.putImageData(id,0,0);
        state.history.push({icon:"🎨",label:"Color Balance"});
      }}break;
      case C.SEL_FROM_ALPHA:{pushUndo("Sel from Alpha");const ns=selFromAlpha(pl.idx??state.act,pl.mode||"replace");
        if(ns){if(pl.add)selMerge(ns);else if(pl.sub)selSubtract(ns);else setSel(ns);}}break;
      case C.GROUP_ADD:{pushUndo("Add Group");const gl=mkGroupL("Group "+(state.layers.filter(l=>l.type==="group").length+1),state.w,state.h);state.layers.push(gl);state.act=state.layers.length-1;state.history.push({icon:"📁",label:"Add Group"});}break;
      case C.GROUP_TOGGLE:{const li=pl.idx??state.act;const L=state.layers[li];if(L&&L.type==="group")L.collapsed=!L.collapsed;}break;
      case C.GROUP_MOVE_IN:{
        // Move layer at pl.layerIdx into the group at pl.groupIdx
        const li=pl.layerIdx,gi=pl.groupIdx;
        const L=state.layers[li],G=state.layers[gi];
        if(!L||!G||G.type!=="group"||li===gi)break;
        if(L.type==="group")break; // Don't nest groups
        if(!G.children)G.children=[];
        if(G.children.includes(li))break; // Already in this group
        pushUndo("Move to Group");
        // Remove from any other group first
        for(const gl of state.layers){if(gl.type==="group"&&gl.children)gl.children=gl.children.filter(ci=>ci!==li);}
        G.children.push(li);
        state.history.push({icon:"📁",label:"→ "+G.name});
      }break;
      case C.GROUP_MOVE_OUT:{
        // Remove layer at pl.layerIdx from the group at pl.groupIdx
        const li=pl.layerIdx,gi=pl.groupIdx;
        const G=state.layers[gi];
        if(!G||G.type!=="group"||!G.children)break;
        pushUndo("Remove from Group");
        G.children=G.children.filter(ci=>ci!==li);
        state.history.push({icon:"📁",label:"← "+G.name});
      }break;
      case C.LAYER_RENAME:{const li=pl.idx??state.act;const L=state.layers[li];if(L&&pl.name){pushUndo("Rename");L.name=pl.name;state.history.push({icon:"✏",label:"Rename → "+pl.name.slice(0,15)});}}break;
      case C.CONTENT_FILL:{if(!state.sel)break;const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}pushUndo("Content-Aware Fill");
        const id=L.x.getImageData(0,0,state.w,state.h),d=id.data,m=state.sel.mask,w=state.w,h=state.h;
        // Build distance map from each selected pixel to nearest non-selected pixel
        const maxSearch=24;
        for(let y=0;y<h;y++)for(let x=0;x<w;x++){
          const idx=y*w+x;
          if(m[idx]<128)continue;
          // Find nearest non-selected pixel
          let bestDist=maxSearch+1,bestX=-1,bestY=-1;
          for(let dy=-maxSearch;dy<=maxSearch&&bestDist>1;dy++)for(let dx=-maxSearch;dx<=maxSearch&&bestDist>1;dx++){
            const nx=x+dx,ny=y+dy;
            if(nx<0||nx>=w||ny<0||ny>=h)continue;
            if(m[ny*w+nx]>=128)continue;
            const dist=Math.sqrt(dx*dx+dy*dy);
            if(dist<bestDist){bestDist=dist;bestX=nx;bestY=ny;}
          }
          if(bestX>=0){
            // Sample a small neighborhood around the source and blend
            const pi=idx*4;let rr=0,gg=0,bb=0,aa=0,wt=0;
            const nr=3;
            for(let dy2=-nr;dy2<=nr;dy2++)for(let dx2=-nr;dx2<=nr;dx2++){
              const sx=bestX+dx2,sy=bestY+dy2;
              if(sx<0||sx>=w||sy<0||sy>=h)continue;
              if(m[sy*w+sx]>=128)continue;
              const si=(sy*w+sx)*4,w2=1/(1+Math.abs(dx2)+Math.abs(dy2));
              rr+=d[si]*w2;gg+=d[si+1]*w2;bb+=d[si+2]*w2;aa+=d[si+3]*w2;wt+=w2;
            }
            if(wt>0){d[pi]=(rr/wt)|0;d[pi+1]=(gg/wt)|0;d[pi+2]=(bb/wt)|0;d[pi+3]=(aa/wt)|0;}
          }
        }
        L.x.putImageData(id,0,0);
        state.history.push({icon:"🧩",label:"Content-Aware Fill"});
      }break;
      // ════════════════ PEN TOOL ════════════════
      case C.PEN_TO_SEL:{
        // Convert pen path to selection mask (supports multiple isolated shapes via _break flag)
        const pts=pl.points;if(!pts||pts.length<2)break;
        pushUndo("Path → Selection");
        const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d",{willReadFrequently:true});
        tx.fillStyle="#fff";
        tx.beginPath();
        tx.moveTo(pts[0].x,pts[0].y);
        for(let i=1;i<pts.length;i++){
          const cur=pts[i];
          if(cur._break){
            // Close current sub-path and start new one
            if(pl.closed!==false)tx.closePath();
            tx.moveTo(cur.x,cur.y);continue;
          }
          const prev=pts[i-1];
          const c1x=prev.cp2x!=null?prev.cp2x:prev.x;
          const c1y=prev.cp2y!=null?prev.cp2y:prev.y;
          const c2x=cur.cp1x!=null?cur.cp1x:cur.x;
          const c2y=cur.cp1y!=null?cur.cp1y:cur.y;
          tx.bezierCurveTo(c1x,c1y,c2x,c2y,cur.x,cur.y);
        }
        if(pl.closed!==false){tx.closePath();}
        tx.fill();
        const id=tx.getImageData(0,0,state.w,state.h);
        const m=new Uint8Array(state.w*state.h);
        let minX=state.w,minY=state.h,maxX=0,maxY=0;
        for(let i=0;i<m.length;i++){
          m[i]=id.data[i*4+3];
          if(m[i]>0){const px=i%state.w,py=(i/state.w)|0;minX=Math.min(minX,px);minY=Math.min(minY,py);maxX=Math.max(maxX,px);maxY=Math.max(maxY,py);}
        }
        if(maxX>=minX){
          const ns={mask:m,x:minX,y:minY,w:maxX-minX+1,h:maxY-minY+1,t:"pen"};
          if(pl.add)selMerge(ns);else if(pl.sub)selSubtract(ns);else setSel(ns);
          state.dirty=true;
          state.history.push({icon:"✒",label:"Path → Selection"});
        }
      }break;
      case C.PEN_STROKE:{
        // Stroke pen path onto active layer (supports multiple isolated shapes via _break)
        const pts=pl.points;if(!pts||pts.length<2)break;
        const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}pushUndo("Stroke Path");
        const cx=L.x;
        cx.save();
        const sr=pl.r!=null?pl.r:(state.p.brushR!=null?state.p.brushR:0);
        const sg=pl.g!=null?pl.g:(state.p.brushG!=null?state.p.brushG:0);
        const sb=pl.b!=null?pl.b:(state.p.brushB!=null?state.p.brushB:0);
        cx.strokeStyle=`rgb(${sr},${sg},${sb})`;
        cx.lineWidth=pl.size!=null?pl.size:(state.p.brushSize||4);
        cx.lineCap="round";cx.lineJoin="round";
        cx.globalAlpha=pl.opacity!=null?pl.opacity:(state.p.brushOpacity!=null?state.p.brushOpacity:1);
        cx.beginPath();
        cx.moveTo(pts[0].x,pts[0].y);
        for(let i=1;i<pts.length;i++){
          const cur=pts[i];
          if(cur._break){
            if(pl.closed!==false)cx.closePath();
            cx.moveTo(cur.x,cur.y);continue;
          }
          const prev=pts[i-1];
          const c1x=prev.cp2x!=null?prev.cp2x:prev.x;
          const c1y=prev.cp2y!=null?prev.cp2y:prev.y;
          const c2x=cur.cp1x!=null?cur.cp1x:cur.x;
          const c2y=cur.cp1y!=null?cur.cp1y:cur.y;
          cx.bezierCurveTo(c1x,c1y,c2x,c2y,cur.x,cur.y);
        }
        if(pl.closed!==false){cx.closePath();}
        cx.stroke();
        cx.restore();
        state.dirty=true;
        state.history.push({icon:"✒",label:"Stroke Path"});
      }break;
      case C.PEN_FILL:{
        // Fill pen path onto active layer (supports multiple isolated shapes via _break)
        const pts=pl.points;if(!pts||pts.length<2)break;
        const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}pushUndo("Fill Path");
        const cx=L.x;
        cx.save();
        const sr=pl.r!=null?pl.r:(state.p.brushR!=null?state.p.brushR:0);
        const sg=pl.g!=null?pl.g:(state.p.brushG!=null?state.p.brushG:0);
        const sb=pl.b!=null?pl.b:(state.p.brushB!=null?state.p.brushB:0);
        cx.fillStyle=`rgb(${sr},${sg},${sb})`;
        cx.globalAlpha=pl.opacity!=null?pl.opacity:(state.p.brushOpacity!=null?state.p.brushOpacity:1);
        cx.beginPath();
        cx.moveTo(pts[0].x,pts[0].y);
        for(let i=1;i<pts.length;i++){
          const cur=pts[i];
          if(cur._break){
            if(pl.closed!==false)cx.closePath();
            cx.moveTo(cur.x,cur.y);continue;
          }
          const prev=pts[i-1];
          const c1x=prev.cp2x!=null?prev.cp2x:prev.x;
          const c1y=prev.cp2y!=null?prev.cp2y:prev.y;
          const c2x=cur.cp1x!=null?cur.cp1x:cur.x;
          const c2y=cur.cp1y!=null?cur.cp1y:cur.y;
          cx.bezierCurveTo(c1x,c1y,c2x,c2y,cur.x,cur.y);
        }
        if(pl.closed!==false){cx.closePath();}
        cx.fill();
        cx.restore();
        state.dirty=true;
        state.history.push({icon:"✒",label:"Fill Path"});
      }break;
      // v0.8: Duplicate layer
      case C.DUPLICATE_LAYER:{
        const srcIdx=pl.idx??state.act;const src=state.layers[srcIdx];if(!src)break;
        pushUndo("Duplicate Layer");
        const nl=mkL(src.name+" Copy",state.w,state.h);
        nl.x.drawImage(src.c,0,0);
        nl.op=src.op;nl.blend=src.blend;nl.vis=src.vis;
        nl.fx={...src.fx};
        if(src.maskEnabled){nl.maskEnabled=true;nl.maskCtx.drawImage(src.mask,0,0);}
        nl.maskLinked=src.maskLinked;
        if(src.textData)nl.textData={...src.textData};
        if(src._psdTextData)nl._psdTextData=src._psdTextData;
        if(src.type==="vector"&&src.vectorData){nl.type="vector";nl.vectorData={paths:JSON.parse(JSON.stringify(src.vectorData.paths)),style:{...src.vectorData.style}};nl._vectorDirty=false;}
        state.layers.splice(srcIdx+1,0,nl);state.act=srcIdx+1;
        // Fix group child indices
        for(const gl of state.layers){if(gl.type==="group"&&gl.children){
          gl.children=gl.children.map(ci=>ci>srcIdx?ci+1:ci);
        }}
        state.history.push({icon:"⊕",label:"Duplicate "+src.name.slice(0,12)});
      }break;
      case C.VECTOR_LAYER_ADD:{
        pushUndo("Add Vector Layer");
        const paths=pl.paths||[];
        const style=pl.style||{};
        const name=pl.name||("Vector "+(state.layers.filter(l=>l.type==="vector").length+1));
        const nl=mkVectorL(name,state.w,state.h,paths,style);
        renderVectorLayer(nl);
        state.layers.push(nl);state.act=state.layers.length-1;
        state.history.push({icon:"✒",label:"Add "+name.slice(0,16)});
      }break;
      case C.VECTOR_LAYER_UPDATE:{
        const li=pl.idx??state.act;const L=state.layers[li];
        if(!L||L.type!=="vector")break;
        pushUndo("Update Vector");
        if(pl.paths!==undefined)L.vectorData.paths=pl.paths;
        if(pl.style)L.vectorData.style={...L.vectorData.style,...pl.style};
        if(pl.name)L.name=pl.name;
        L._vectorDirty=true;
        renderVectorLayer(L);
        state.history.push({icon:"✒",label:"Update Vector"});
      }break;

      // ══════════════════════════════════════════════════════════
      // Phase 4: Retouching Tools
      // ══════════════════════════════════════════════════════════

      // ════════════════ RETOUCHING (Phase 4) ════════════════
      case C.SPOT_HEAL:{
        // Spot Healing Brush — sample surrounding pixels and blend into target area
        const L=state.layers[state.act];if(!L||!L.vis)break;
        pushUndo("Spot Heal");
        const cx=L.x;
        const x=Math.round(pl.x),y=Math.round(pl.y),rad=Math.round((pl.size||state.p.brushSize||20)/2);
        const margin=Math.ceil(rad*1.5);
        const x0=Math.max(0,x-margin),y0=Math.max(0,y-margin);
        const x1=Math.min(state.w,x+margin),y1=Math.min(state.h,y+margin);
        const rw=x1-x0,rh=y1-y0;
        if(rw<2||rh<2)break;
        const id=cx.getImageData(x0,y0,rw,rh);const d=id.data;
        // For each pixel inside the heal radius, replace with weighted average of neighbors outside the radius
        for(let py=0;py<rh;py++)for(let px=0;px<rw;px++){
          const dx2=(px+x0)-x,dy2=(py+y0)-y;
          const dist=Math.sqrt(dx2*dx2+dy2*dy2);
          if(dist>rad)continue;
          // Sample from the ring just outside the heal radius
          let sr=0,sg=0,sb=0,sw=0;
          const sampleRad=rad+4;
          for(let a=0;a<Math.PI*2;a+=Math.PI/8){
            const sx=Math.round(px+Math.cos(a)*sampleRad);
            const sy=Math.round(py+Math.sin(a)*sampleRad);
            if(sx<0||sx>=rw||sy<0||sy>=rh)continue;
            const si=(sy*rw+sx)*4;
            const w=1/(1+Math.abs(a));
            sr+=d[si]*w;sg+=d[si+1]*w;sb+=d[si+2]*w;sw+=w;
          }
          if(sw>0){
            const blend=1-dist/rad; // stronger in center
            const i=(py*rw+px)*4;
            d[i]=Math.round(d[i]*(1-blend)+sr/sw*blend);
            d[i+1]=Math.round(d[i+1]*(1-blend)+sg/sw*blend);
            d[i+2]=Math.round(d[i+2]*(1-blend)+sb/sw*blend);
          }
        }
        cx.putImageData(id,x0,y0);
        state.history.push({icon:"🩹",label:"Spot Heal"});
      }break;

      case C.RED_EYE:{
        // Red-Eye Removal — detect red region around click point, desaturate + darken
        const L=state.layers[state.act];if(!L||!L.vis)break;
        pushUndo("Red-Eye");
        const cx=L.x;
        const x=Math.round(pl.x),y=Math.round(pl.y),rad=Math.round((pl.size||30)/2);
        const x0=Math.max(0,x-rad),y0=Math.max(0,y-rad);
        const x1=Math.min(state.w,x+rad),y1=Math.min(state.h,y+rad);
        const rw=x1-x0,rh=y1-y0;
        if(rw<2||rh<2)break;
        const id=cx.getImageData(x0,y0,rw,rh);const d=id.data;
        for(let py=0;py<rh;py++)for(let px=0;px<rw;px++){
          const dx2=(px+x0)-x,dy2=(py+y0)-y;
          if(dx2*dx2+dy2*dy2>rad*rad)continue;
          const i=(py*rw+px)*4;
          const r=d[i],g=d[i+1],b=d[i+2];
          // Detect red: high red channel, low green+blue, above brightness threshold
          const redRatio=r/(g+b+1);
          if(redRatio>1.2&&r>60){
            // Desaturate and darken the red channel
            const avg=Math.round((r+g+b)/3);
            const strength=Math.min(1,(redRatio-1.2)/1.5); // blend based on how red
            d[i]=Math.round(r*(1-strength)+avg*0.4*strength);
            d[i+1]=Math.round(g*(1-strength)+avg*0.4*strength);
            d[i+2]=Math.round(b*(1-strength)+avg*0.4*strength);
          }
        }
        cx.putImageData(id,x0,y0);
        state.history.push({icon:"👁",label:"Red-Eye Removal"});
      }break;

      case C.FREQ_SEP:{
        // Frequency Separation — split active layer into low-frequency (color) + high-frequency (detail)
        const L=state.layers[state.act];if(!L||!L.vis)break;
        pushUndo("Frequency Separation");
        const w=state.w,h=state.h;
        const cx=L.x;
        const origData=cx.getImageData(0,0,w,h);
        // Create low-frequency (gaussian blur)
        const blurRad=pl.radius||4;
        const lowCanvas=new OffscreenCanvas(w,h);const lowCtx=lowCanvas.getContext("2d",{willReadFrequently:true});
        lowCtx.putImageData(origData,0,0);
        // 3-pass box blur approximation
        for(let pass=0;pass<3;pass++){
          const src=lowCtx.getImageData(0,0,w,h);const dst=lowCtx.createImageData(w,h);
          const sd=src.data,dd=dst.data;
          for(let y2=0;y2<h;y2++)for(let x2=0;x2<w;x2++){
            let sr=0,sg=0,sb=0,sa=0,cnt=0;
            for(let k=-blurRad;k<=blurRad;k++){
              const sx=Math.max(0,Math.min(w-1,x2+k));
              const si=(y2*w+sx)*4;
              sr+=sd[si];sg+=sd[si+1];sb+=sd[si+2];sa+=sd[si+3];cnt++;
            }
            const di=(y2*w+x2)*4;
            dd[di]=sr/cnt;dd[di+1]=sg/cnt;dd[di+2]=sb/cnt;dd[di+3]=sa/cnt;
          }
          lowCtx.putImageData(dst,0,0);
        }
        const lowData=lowCtx.getImageData(0,0,w,h);
        // Create high-frequency (original - low + 128) using proper layer factory
        const hiLayer=mkL((L.name||"Layer").replace(/ \(Low\)$/,"")+" (High)",w,h);
        const highData=hiLayer.x.createImageData(w,h);
        const od=origData.data,ld=lowData.data,hd=highData.data;
        for(let i=0;i<od.length;i+=4){
          hd[i]=Math.max(0,Math.min(255,od[i]-ld[i]+128));
          hd[i+1]=Math.max(0,Math.min(255,od[i+1]-ld[i+1]+128));
          hd[i+2]=Math.max(0,Math.min(255,od[i+2]-ld[i+2]+128));
          hd[i+3]=255;
        }
        hiLayer.x.putImageData(highData,0,0);
        hiLayer.blend="overlay";
        hiLayer.type="pixel";
        // Replace active layer canvas with low-freq data
        cx.clearRect(0,0,w,h);cx.drawImage(lowCanvas,0,0);
        L.name=(L.name||"Layer")+" (Low)";
        // Insert high-freq layer above active
        state.layers.splice(state.act+1,0,hiLayer);
        state.history.push({icon:"📊",label:"Frequency Separation"});
      }break;
      // ════════════════ ADVANCED TRANSFORM & SELECTION (Phase 8) ════════════════
      case C.LAYER_FLIP_H:{const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}
        pushUndo("Flip Horizontal");
        const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d");
        tx.translate(state.w,0);tx.scale(-1,1);tx.drawImage(L.c,0,0);
        L.x.clearRect(0,0,state.w,state.h);L.x.drawImage(tc,0,0);
        state.history.push({icon:"↔",label:"Flip H"});
      }break;
      case C.LAYER_FLIP_V:{const L=state.layers[state.act];if(!L||!L.vis){state.onHiddenLayerAttempt?.();break;}
        pushUndo("Flip Vertical");
        const tc=new OffscreenCanvas(state.w,state.h);const tx=tc.getContext("2d");
        tx.translate(0,state.h);tx.scale(1,-1);tx.drawImage(L.c,0,0);
        L.x.clearRect(0,0,state.w,state.h);L.x.drawImage(tc,0,0);
        state.history.push({icon:"↕",label:"Flip V"});
      }break;
      case C.SEL_COLOR_RANGE:{
        const L=state.layers[state.act];if(!L)break;
        pushUndo("Color Range");
        const id=L.x.getImageData(0,0,state.w,state.h);const d=id.data;
        const sx=Math.round(pl.x),sy=Math.round(pl.y);
        if(sx<0||sx>=state.w||sy<0||sy>=state.h)break;
        const si=(sy*state.w+sx)*4;
        const sr=d[si],sg=d[si+1],sb=d[si+2];
        const fuzz=(pl.fuzziness||30)/100; // 0-1 normalized
        // Convert sample to HSL
        const toHSL=(r,g,b)=>{r/=255;g/=255;b/=255;const mx=Math.max(r,g,b),mn=Math.min(r,g,b),dl=mx-mn;let h=0,s=0;const l2=(mx+mn)/2;if(dl>0){s=l2>0.5?dl/(2-mx-mn):dl/(mx+mn);if(mx===r)h=((g-b)/dl+(g<b?6:0))/6;else if(mx===g)h=((b-r)/dl+2)/6;else h=((r-g)/dl+4)/6;}return[h,s,l2];};
        const[sh,ss,sl]=toHSL(sr,sg,sb);
        const mask=new Uint8Array(state.w*state.h);
        for(let i=0;i<mask.length;i++){
          const pi=i*4;if(d[pi+3]<1)continue;
          const[ph,ps,pl2]=toHSL(d[pi],d[pi+1],d[pi+2]);
          // HSL distance (hue wraps around)
          let dh=Math.abs(ph-sh);if(dh>0.5)dh=1-dh;
          const dist=Math.sqrt(dh*dh*4+((ps-ss)*(ps-ss))+(pl2-sl)*(pl2-sl));
          if(dist<=fuzz)mask[i]=255;
          else if(dist<=fuzz*1.5)mask[i]=Math.round(255*(1-(dist-fuzz)/(fuzz*0.5)));
        }
        const ns={mask,x:0,y:0,w:state.w,h:state.h,t:"alpha"};
        if(pl.add)selMerge(ns);else if(pl.sub)selSubtract(ns);else setSel(ns);
        state.history.push({icon:"🎨",label:"Color Range"});
      }break;
    }
  }

  return{
    s: state,
    /** Inject the WasmBridge — engine will route pixel kernels through wasm when available */
    setBridge(bridge){state._bridge=bridge;},
    pushParams(rd) {
      state.p = {
        cursorX:         rd("cursorX"),
        cursorY:         rd("cursorY"),
        cursorDown:      rd("cursorDown"),
        brushSize:       rd("brushSize"),
        brushOpacity:    rd("brushOpacity"),
        brushR:          rd("brushR"),
        brushG:          rd("brushG"),
        brushB:          rd("brushB"),
        activeTool:      rd("activeTool"),
        activeLayer:     rd("activeLayer"),
        brightness:      rd("brightness"),
        contrast:        rd("contrast"),
        saturation:      rd("saturation"),
        brushHardness:   rd("brushHardness"),
        brushTip:        rd("brushTip"),
        pressure:        rd("pressure"),
        pressureSize:    rd("pressureSize"),
        pressureOpacity: rd("pressureOpacity"),
        // Phase 3 brush dynamics
        brushSpacing:    rd("brushSpacing"),
        brushFlow:       rd("brushFlow"),
        sizeJitter:      rd("sizeJitter"),
        angleJitter:     rd("angleJitter"),
        opacityJitter:   rd("opacityJitter"),
        scatterAmount:   rd("scatterAmount"),
        brushSmoothing:  rd("brushSmoothing"),
        pressureHardness:rd("pressureHardness"),
        pressureFlow:    rd("pressureFlow"),
        pressureCurve:   rd("pressureCurve"),
        tiltX:           rd("tiltX"),
        tiltY:           rd("tiltY"),
      };
      state.act = Math.min(state.p.activeLayer, state.layers.length - 1);
    },
    pushCommand:handleCommand,
    tick() {
      const isDrawingTool = state.p.activeTool === T.BRUSH
        || state.p.activeTool === T.ERASER
        || state.p.activeTool === T.RUBBER_STAMP
        || state.p.activeTool === T.DODGE
        || state.p.activeTool === T.BURN
        || state.p.activeTool === T.DISTORT
        || state.p.activeTool === T.SPOT_HEAL;

      if (state.strokeOn && state.p.cursorDown && isDrawingTool) {
        if (state.p.activeTool === T.SPOT_HEAL) {
          // Continuous spot heal along drag path
          spotHealAt(state.p.cursorX, state.p.cursorY, state.p.brushSize || 20);
        } else {
          brush();
        }

        // Mark active layer as GPU-dirty for GL compositor re-upload
        const activeLayer = state.layers[state.act];
        if (activeLayer) {
          activeLayer._glDirty = true;
          activeLayer._fxDirty = true;
          activeLayer._undoClean = false;
        }

        // Mark adjustment layers above the active layer as dirty
        // so they re-composite with the updated pixel data
        for (let i = state.act + 1; i < state.layers.length; i++) {
          const layer = state.layers[i];
          if (layer.type === "adjustment") {
            layer._adjDirty = true;
            layer._glDirty = true;
          }
        }
      }
    },
    render(ctx,cw,ch){
      // Only set smoothing once — avoid per-frame state changes
      if(!state._smoothingSet){ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality="high";state._smoothingSet=true;}

      // ── WebGL2 fast path — Alloyhead framework GLCompositor ──
      if(!state._glTried){
        state._glTried=true;
        try{
          state._glCanvas=new OffscreenCanvas(state.w,state.h);
          state._gl=new GLCompositor(state._glCanvas);
          if(state._gl.ready)console.log("[PixAlloy] Alloyhead GLCompositor active");
          else{state._gl=null;console.log("[PixAlloy] WebGL2 unavailable, using Canvas2D");}
        }catch(e){state._gl=null;console.warn("[PixAlloy] GL init failed:",e);}
      }
      if(state._gl&&state._gl.ready){
        // Resize GL canvas if needed
        if(state._glCanvas.width!==state.w||state._glCanvas.height!==state.h){
          state._glCanvas.width=state.w;state._glCanvas.height=state.h;
        }
        // Map PixAlloy layers → framework GLCompositeLayer[]
        const glLayers=[];
        for(let li=0;li<state.layers.length;li++){
          const l=state.layers[li];
          if(!l.vis)continue;
          if(state.tfm&&li===state.act&&!state.tfm.fromSel)continue;
          if(l.type==="group")continue; // TODO: group compositing in GL
          if (l.type === "adjustment" && l.adjType && l.adjParams) {
            const effectiveParams = l._adjPreviewParams ?? l.adjParams;
            const isDirty = l._glDirty !== false || l._adjDirty;
            // GL compositor only has shaders for brightness, levels, huesat, invert.
            // All other adjustment types use CPU fallback: read current composite,
            // apply adjustment, write back as a raster layer for GL to composite.
            const glSupported = l.adjType===ADJ.BRIGHTNESS||l.adjType===ADJ.LEVELS||l.adjType===ADJ.HUESAT||l.adjType===ADJ.INVERT;
            if (glSupported) {
              glLayers.push({
                source: l.c, opacity: l.op, blend: l.blend || "source-over",
                isAdjustment: true, adjType: l.adjType, adjParams: effectiveParams,
                id: li, dirty: isDirty,
              });
            } else {
              // CPU fallback: composite what we have so far via GL, then apply adjustment
              // We render into _adjCache and push that as a regular raster layer
              if (isDirty || !l._adjCache || l._adjCacheW!==state.w || l._adjCacheH!==state.h) {
                // Render current GL layers to get the composite-so-far
                if (glLayers.length > 0) {
                  const partialOk = state._gl.composite(glLayers, state.w, state.h);
                  if (partialOk) {
                    if (!l._adjCache) l._adjCache = new OffscreenCanvas(state.w, state.h);
                    if (l._adjCache.width !== state.w || l._adjCache.height !== state.h) {
                      l._adjCache.width = state.w; l._adjCache.height = state.h;
                    }
                    const acx = l._adjCache.getContext("2d", {willReadFrequently: true});
                    acx.clearRect(0, 0, state.w, state.h);
                    acx.drawImage(state._glCanvas, 0, 0);
                    const adj_id = acx.getImageData(0, 0, state.w, state.h);
                    applyAdjustment(adj_id, l.adjType, effectiveParams, state._bridge);
                    acx.putImageData(adj_id, 0, 0);
                    l._adjCacheW = state.w; l._adjCacheH = state.h;
                  }
                }
                l._adjDirty = false;
              }
              // Replace all GL layers so far with the adjusted cache
              glLayers.length = 0;
              if (l._adjCache) {
                glLayers.push({
                  source: l._adjCache, opacity: 1, blend: "source-over",
                  id: li, dirty: true,
                });
              }
            }
            l._adjDirty = false;
            l._glDirty = false;
          } else {
            // Raster layer: upload texture when dirty
            glLayers.push({
              source: l.c, opacity: l.op, blend: l.blend || "source-over",
              id: li, dirty: l._glDirty !== false,
            });
            l._glDirty = false;
          }
        }
        const ok=state._gl.composite(glLayers,state.w,state.h);
        if(ok){
          const bmp=state._glCanvas.transferToImageBitmap();
          // Draw GL result first, then checkerboard behind transparent areas
          // This saves one full-canvas drawImage vs drawing checkerboard first
          ctx.globalCompositeOperation="copy";
          ctx.drawImage(bmp,0,0,cw,ch);
          bmp.close();
          ctx.globalCompositeOperation="destination-over";
          ctx.drawImage(renderCache.getCheckerboard(cw,ch),0,0);
          ctx.globalCompositeOperation="source-over";
          // ── FX post-pass: apply layer effects on top of GL output ──
          // The GL compositor doesn't handle layer effects (shadow, glow, etc.)
          // Render into a temp canvas at engine resolution, then scale to viewport.
          let anyFx=false;
          for(let li=0;li<state.layers.length;li++){
            const l=state.layers[li];
            if(!l.vis||l.type==="adjustment"||l.type==="group")continue;
            if(l.fx&&(l.fx.shadow||l.fx.bevel||l.fx.outline||l.fx.innerShadow||l.fx.outerGlow||l.fx.innerGlow||l.fx.colorOverlay||l.fx.gradOverlay)){anyFx=true;break;}
          }
          if(anyFx){
            const{canvas:fxc,ctx:fxx}=renderCache.getCanvas('glFxPass',state.w,state.h);
            fxx.clearRect(0,0,state.w,state.h);
            for(let li=0;li<state.layers.length;li++){
              const l=state.layers[li];
              if(!l.vis||l.type==="adjustment"||l.type==="group")continue;
              const hasFx=l.fx&&(l.fx.shadow||l.fx.bevel||l.fx.outline||l.fx.innerShadow||l.fx.outerGlow||l.fx.innerGlow||l.fx.colorOverlay||l.fx.gradOverlay);
              if(!hasFx)continue;
              renderLayerFx(fxx,l);
            }
            ctx.drawImage(fxc,0,0,cw,ch);
          }
          return;
        }
      }

      // ── Canvas2D fallback ──
      // Checkerboard from framework RenderCache
      ctx.drawImage(renderCache.getCheckerboard(cw,ch),0,0);
      for(let li=0;li<state.layers.length;li++){
        const l=state.layers[li];
        if(!l.vis)continue;
        // Skip active layer when full-layer transform is active — tfmH overlay draws the preview
        // For selection-based transform, still draw the layer (it has the non-selected pixels)
        if(state.tfm&&li===state.act&&!state.tfm.fromSel)continue;
        // ── Group layer: render children into temp canvas, composite as unit ──
        if(l.type==="group"&&l.children&&l.children.length>0){
          const{canvas:gc,ctx:gx2}=renderCache.getCanvas('group',state.w,state.h);
          gx2.clearRect(0,0,state.w,state.h);
          for(const ci of l.children){
            const cl=state.layers[ci];if(!cl||!cl.vis)continue;
            if(cl.type==="adjustment"&&cl.adjType&&cl.adjParams){
              const adj_id=gx2.getImageData(0,0,state.w,state.h);applyAdjustment(adj_id,cl.adjType,cl._adjPreviewParams??cl.adjParams,state._bridge);gx2.putImageData(adj_id,0,0);continue;
            }
            gx2.globalAlpha=cl.op;gx2.globalCompositeOperation=cl.blend||"source-over";gx2.drawImage(cl.c,0,0);
            gx2.globalAlpha=1;gx2.globalCompositeOperation="source-over";
          }
          ctx.save();ctx.globalAlpha=l.op;ctx.globalCompositeOperation=l.blend||"source-over";
          ctx.drawImage(gc,0,0);ctx.restore();continue;
        }
        // ── Adjustment layer: apply transform to composite so far ──
        if(l.type==="adjustment"&&l.adjType&&l.adjParams){
          try{
          const effectiveParams=l._adjPreviewParams??l.adjParams;
          // Cache invalidation: numeric hash for simple params, JSON hash for complex (arrays/objects)
          const vals=Object.values(effectiveParams);
          let paramsHash=vals.length;
          let hasComplex=false;
          for(let vi=0;vi<vals.length;vi++){
            const v=vals[vi];
            if(typeof v==='number')paramsHash=((paramsHash*31)+(v*1e6|0))|0;
            else if(typeof v==='string')for(let ci=0;ci<v.length;ci++)paramsHash=((paramsHash*31)+v.charCodeAt(ci))|0;
            else hasComplex=true;
          }
          // For complex params (arrays/objects like gradient stops), compute a string key for comparison
          if(hasComplex){
            const complexKey=JSON.stringify(effectiveParams);
            if(l._adjComplexKey===complexKey&&l._adjCache&&!l._adjDirty){
              ctx.save();ctx.globalCompositeOperation="copy";ctx.drawImage(l._adjCache,0,0);ctx.restore();continue;
            }
            l._adjComplexKey=complexKey;
          }
          const needsRecompute=!l._adjCache||l._adjCacheW!==state.w||l._adjCacheH!==state.h||l._adjCacheHash!==paramsHash||l._adjDirty;
          if(needsRecompute){
            const adj_id=ctx.getImageData(0,0,state.w,state.h);
            applyAdjustment(adj_id,l.adjType,effectiveParams,state._bridge);
            ctx.putImageData(adj_id,0,0);
            if(!l._adjCache)l._adjCache=new OffscreenCanvas(state.w,state.h);
            if(l._adjCache.width!==state.w)l._adjCache.width=state.w;
            if(l._adjCache.height!==state.h)l._adjCache.height=state.h;
            l._adjCache.getContext("2d",{willReadFrequently:true}).putImageData(adj_id,0,0);
            l._adjCacheHash=paramsHash;l._adjCacheW=state.w;l._adjCacheH=state.h;l._adjDirty=false;
          } else {
            ctx.save();ctx.globalCompositeOperation="copy";
            ctx.drawImage(l._adjCache,0,0);
            ctx.restore();
          }
          }catch(err){console.error("Adj render error:",err,l.adjType,l.adjParams);}
          continue;
        }
        ctx.save();
        ctx.globalAlpha=l.op;
        ctx.globalCompositeOperation=l.blend||"source-over";
        if(l.maskEnabled){const{canvas:tmp,ctx:tx}=renderCache.getCanvas('mask',state.w,state.h);tx.clearRect(0,0,state.w,state.h);tx.drawImage(l.c,0,0);tx.globalCompositeOperation="destination-in";tx.drawImage(l.mask,0,0);tx.globalCompositeOperation="source-over";ctx.drawImage(tmp,0,0);}
        else ctx.drawImage(l.c,0,0);
        ctx.restore();
        // All layer effects (drop shadow, bevel, outline, inner shadow, glows, overlays)
        renderLayerFx(ctx,l);
      }
      ctx.globalAlpha=1;ctx.globalCompositeOperation="source-over";
      if(state.clipOverlay){const id=ctx.getImageData(0,0,cw,ch),d=id.data;for(let i=0;i<d.length;i+=4){if(d[i+3]<10)continue;const mx2=Math.max(d[i],d[i+1],d[i+2]),mn=Math.min(d[i],d[i+1],d[i+2]);if(mx2>=250){d[i]=255;d[i+1]=0;d[i+2]=0;d[i+3]=180;}else if(mn<=5){d[i]=0;d[i+1]=0;d[i+2]=255;d[i+3]=180;}}ctx.putImageData(id,0,0);}
    },
    loadImg(src){
      const nw=src.width,nh=src.height;state.w=nw;state.h=nh;
      // Reset to single background layer
      state.layers=[mkL("Background",nw,nh)];state.act=0;
      // Draw the source image - try multiple approaches for sandbox compatibility
      const L=state.layers[0];
      try{
        L.x.drawImage(src,0,0);
        // Verify it actually drew something (some sandboxes silently fail)
        const test=L.x.getImageData(0,0,1,1).data;
        if(test[0]===0&&test[1]===0&&test[2]===0&&test[3]===0&&nw>1&&nh>1)throw new Error("draw silent fail");
      }catch(e2){
        try{
          // Fallback: render through a regular DOM canvas
          const tmp=document.createElement("canvas");tmp.width=nw;tmp.height=nh;
          const tx=tmp.getContext("2d",{willReadFrequently:true});tx.drawImage(src,0,0);
          L.x.putImageData(tx.getImageData(0,0,nw,nh),0,0);
        }catch(e3){
          // Last resort: fill white
          L.x.fillStyle="#fff";L.x.fillRect(0,0,nw,nh);
        }
      }
      state.undo=[];state.redo=[];clearSel();state.tfm=null;state.history=[{icon:"🖼",label:"Open Image"}];
      L._glDirty=true;
      if(state.onDimsChanged)state.onDimsChanged(nw,nh);
    },
    /** Load pre-built layers (from PSD import). Replaces all layers and resets state. */
    loadLayers(layers, w, h, historyLabel){
      state.w=w;state.h=h;
      state.layers=layers;
      state.act=layers.length-1;
      state.undo=[];state.redo=[];clearSel();state.tfm=null;
      state.history=[{icon:"📄",label:historyLabel||"Import"}];
      for(const l of state.layers){l._glDirty=true;l._undoClean=false;}
      if(state.onDimsChanged)state.onDimsChanged(w,h);
    },
    // ── Filter Preview System (1.5) ──
    // Snapshot the active layer for non-destructive preview during slider drag
    filterPreviewBegin(){
      const L=state.layers[state.act];if(!L||!L.vis)return false;
      state._filterBackup=L.x.getImageData(0,0,state.w,state.h);
      state._filterPreviewActive=true;
      return true;
    },
    // Apply filter to a copy of the backup and draw result onto layer canvas (for live preview)
    filterPreviewApply(filterType,params){
      if(!state._filterBackup||!state._filterPreviewActive)return;
      const L=state.layers[state.act];if(!L)return;
      // Restore from backup first
      L.x.putImageData(state._filterBackup,0,0);
      // Apply filter — reuses exact same logic as the engine command handlers
      const id=L.x.getImageData(0,0,state.w,state.h);
      const d=id.data,w=state.w,h=state.h;
      const B=state._bridge;

      if(filterType==="levels"){
        // Copied from C.LEVELS handler (line ~1083)
        const iB=params.inBlack||0,iW=params.inWhite||255,oB=params.outBlack||0,oW=params.outWhite||255,gm=params.gamma||1;
        if(B&&typeof B.run==='function'){B.run("levels",d,w,h,{inBlack:iB,inWhite:iW,gamma:gm,outBlack:oB,outWhite:oW});}
        else{
          const range=iW-iB||1;
          for(let i=0;i<d.length;i+=4){for(let ch=0;ch<3;ch++){let v=(d[i+ch]-iB)/range;v=Math.max(0,Math.min(1,v));v=Math.pow(v,1/gm);d[i+ch]=Math.round(oB+v*(oW-oB));}}
        }
      }else if(filterType==="huesat"){
        // Use KernelRegistry.run() for master (routes to WASM with correct param ordering),
        // JS path for per-range targeting (WASM kernel doesn't support range targeting)
        const range=params.range||"master";
        if(B&&typeof B.run==='function'&&range==="master"){
          B.run("hue_saturation",d,w,h,{hue:params.hue||0,saturation:params.saturation||0,lightness:params.lightness||0});
        }else{
          const hShift=(params.hue||0)/360,sScale=1+(params.saturation||0)/100,lShift=(params.lightness||0)/100*0.5;
          const HUE_RANGES={master:null,reds:0,yellows:1/6,greens:2/6,cyans:3/6,blues:4/6,magentas:5/6};
          const center=HUE_RANGES[range];
          const rangeWidth=1/6;const falloff=rangeWidth*0.5;
          for(let i=0;i<d.length;i+=4){
            if(d[i+3]<1)continue;
            let r2=d[i]/255,g2=d[i+1]/255,b2=d[i+2]/255;
            const mx=Math.max(r2,g2,b2),mn=Math.min(r2,g2,b2),dl=mx-mn;
            let h2=0,ss2=0,l2=(mx+mn)/2;
            if(dl>0){ss2=l2>0.5?dl/(2-mx-mn):dl/(mx+mn);if(mx===r2)h2=((g2-b2)/dl+(g2<b2?6:0))/6;else if(mx===g2)h2=((b2-r2)/dl+2)/6;else h2=((r2-g2)/dl+4)/6;}
            let strength=1;
            if(center!=null&&dl>0.01){let dist=Math.abs(h2-center);if(dist>0.5)dist=1-dist;if(dist>rangeWidth+falloff)strength=0;else if(dist>rangeWidth)strength=1-(dist-rangeWidth)/falloff;}else if(center!=null){strength=0;}
            if(strength<0.001)continue;
            const effH=hShift*strength,effS=1+(sScale-1)*strength,effL=lShift*strength;
            h2=(h2+effH)%1;if(h2<0)h2+=1;ss2=Math.max(0,Math.min(1,ss2*effS));l2=Math.max(0,Math.min(1,l2+effL));
            if(ss2===0){r2=g2=b2=l2;}else{const hue2rgb=(p3,q3,t)=>{if(t<0)t+=1;if(t>1)t-=1;if(t<1/6)return p3+(q3-p3)*6*t;if(t<1/2)return q3;if(t<2/3)return p3+(q3-p3)*(2/3-t)*6;return p3;};const q2=l2<0.5?l2*(1+ss2):l2+ss2-l2*ss2,p2=2*l2-q2;r2=hue2rgb(p2,q2,h2+1/3);g2=hue2rgb(p2,q2,h2);b2=hue2rgb(p2,q2,h2-1/3);}
            d[i]=Math.round(r2*255);d[i+1]=Math.round(g2*255);d[i+2]=Math.round(b2*255);
          }
        }
      }else if(filterType==="gaussblur"){
        // Use the engine's existing gaussian blur function
        L.x.putImageData(id,0,0);
        applyGaussianBlur(params.radius||5);
        L._glDirty=true;state.dirty=true;
        return; // applyGaussianBlur handles putImageData internally
      }else if(filterType==="colorbal"){
        // Use applyAdjustment which handles color balance correctly
        applyAdjustment(id,ADJ.COLORBAL,params,B);
      }
      L.x.putImageData(id,0,0);
      L._glDirty=true;
      state.dirty=true;
    },
    // Commit the current preview result (keep current pixels, push undo, clear backup)
    filterPreviewCommit(label){
      if(!state._filterBackup)return;
      const L=state.layers[state.act];
      if(!L){state._filterBackup=null;state._filterPreviewActive=false;return;}
      // Save the filtered result that's currently on the canvas
      const filteredResult=L.x.getImageData(0,0,state.w,state.h);
      // Restore original pixels so pushUndo snapshots the pre-filter state
      L.x.putImageData(state._filterBackup,0,0);
      pushUndo(label||"Filter");
      // Now put the filtered result back
      L.x.putImageData(filteredResult,0,0);
      L._glDirty=true;
      state._filterBackup=null;
      state._filterPreviewActive=false;
      state.dirty=true;
    },
    // Cancel preview — restore original pixels from backup
    filterPreviewCancel(){
      if(!state._filterBackup)return;
      const L=state.layers[state.act];
      if(L)L.x.putImageData(state._filterBackup,0,0);
      L._glDirty=true;
      state._filterBackup=null;
      state._filterPreviewActive=false;
      state.dirty=true;
    },
    composite(){const c=new OffscreenCanvas(state.w,state.h);const x=c.getContext("2d",{willReadFrequently:true});for(const l of state.layers){if(!l.vis)continue;if(l.type==="adjustment"&&l.adjType&&l.adjParams){const adj_id=x.getImageData(0,0,state.w,state.h);applyAdjustment(adj_id,l.adjType,l.adjParams,state._bridge);x.putImageData(adj_id,0,0);continue;}x.globalAlpha=l.op;x.globalCompositeOperation=l.blend||"source-over";x.drawImage(l.c,0,0);}return c;},
    getHistogram(){
      // Reuse framework RenderCache for histogram composite
      const{canvas:comp,ctx:cx}=renderCache.getCanvas('histComp',state.w,state.h);
      cx.clearRect(0,0,state.w,state.h);
      for(const l of state.layers){if(!l.vis)continue;if(l.type==="adjustment"&&l.adjType&&l.adjParams){const adj_id=cx.getImageData(0,0,state.w,state.h);applyAdjustment(adj_id,l.adjType,l.adjParams,state._bridge);cx.putImageData(adj_id,0,0);continue;}cx.globalAlpha=l.op;cx.globalCompositeOperation=l.blend||"source-over";cx.drawImage(l.c,0,0);}
      try{
      // For large images, sample a downscaled version for histogram (4× faster at 4K)
      const maxDim=1024;
      let sw=state.w,sh=state.h,srcCanvas=comp;
      if(state.w>maxDim||state.h>maxDim){
        const scale=maxDim/Math.max(state.w,state.h);
        sw=Math.round(state.w*scale);sh=Math.round(state.h*scale);
        const{canvas:histDown,ctx:hdx}=renderCache.getCanvas('histDown',sw,sh);
        hdx.drawImage(comp,0,0,sw,sh);
        srcCanvas=histDown;
      }
      const id=srcCanvas.getContext("2d",{willReadFrequently:true}).getImageData(0,0,sw,sh).data;
      const rB=new Array(256).fill(0),gB=new Array(256).fill(0),bB=new Array(256).fill(0),lum=new Array(256).fill(0);
      for(let i=0;i<id.length;i+=4){if(id[i+3]>0){rB[id[i]]++;gB[id[i+1]]++;bB[id[i+2]]++;const lv=Math.round(id[i]*0.299+id[i+1]*0.587+id[i+2]*0.114);lum[Math.min(255,lv)]++;}}return{r:rB,g:gB,b:bB,lum};}catch(e){return null;}
    },
    destroy(){
      // Release GLCompositor GPU resources (textures, FBOs, shaders, quad)
      if(state._gl){state._gl.destroy();state._gl=null;}
      state._glCanvas=null;state._glTried=false;
      // Release per-layer OffscreenCanvases and adjustment caches
      for(const l of state.layers){
        l.c=null;l.x=null;
        if(l._adjCache){l._adjCache=null;}
      }
      state.layers=[];
      // Release undo/redo layer snapshots (each holds OffscreenCanvas data)
      state.undo=[];state.redo=[];
      // Release RenderCache pooled canvases
      renderCache.clear();
    },
  };
}
